/**
 * 家长端 · 学校作业（见 `docs/接口契约对齐.md` 的「作业」章节）。
 *
 * ## 这一页解决什么
 *
 * 学校作业**每天都不一样**，所以主路径是"选日期 → 逐科目录入 → 整单保存"：
 *
 *   选日期（默认今天，可切到任意一天）
 *     → 录科目（名称 + 可选的开始时刻 + 可选的截止时刻**或**时长）
 *       → 每科下面录若干作业项（名称 + 可选分值 / 量化目标）
 *         → 保存（`PUT /students/:id/homework?date=`，**整单**，不是增量）
 *
 * 另外两件事也是刚需：**「复制上次的结构」**（一键把上一次的科目与作业项结构搬过来，
 * **不复制完成状态**）与**查过往某天**（历史清单点一天就翻到那天）。
 *
 * ## 三个必须保真的语义（写错了就是 bug，都由 `lib/api/homework.ts` 的共享判据兜住）
 *
 * 1. **`dueTimeSource === 'dayEnd'`**：科目没填截止时刻时服务端把 `due_time` 落成 `23:59`。
 *    - 展示走 `describeSubjectSchedule()` → 「当天完成」，**不显示 23:59**；
 *    - 回填走 `dueTimeInputOf()` → 截止时刻框留空，**不把 23:59 当成"用户填过的值"**。
 * 2. **`retained: true`**：有真实打卡记录、被移出这天的计划但没被删除的科目/作业项。
 *    界面把它渲染成"已移除"的样子（灰掉 + 「已移除（保留记录）」+ 一行说明），
 *    **它不会出现在孩子端**，所以录入时它也**不进草稿**（不参与"至少 1 项"的校验）。
 *    服务端返回的 `warnings` 原样用人话弹出来。
 * 3. **同名重新加回来会复用原记录**（含原打卡记录）：这是刻意的，界面不特殊处理 ——
 *    只把服务端给的 `status` / `checkin` 如实显示出来，**不当成新项渲染成空的**
 *    （判据是 `isHomeworkItemEmpty()`，依据是"有没有打卡记录"，不是"名字新不新"）。
 *
 * ## 界面取舍
 *
 * - **手机优先**：科目与作业项都是**卡片式纵向排布**，不用宽表格（那句话在窄屏会被挤成
 *   一个字一行）。每个科目卡片里一行一个作业项输入框，宽度靠 `min-w-0 flex-1` 撑住。
 * - ⚠️ **「一段说明文字 + 右侧按钮/控件」的行，窄屏必须让文字独占整行**（`flex-col`
 *   → `sm:flex-row`，与页面里那几个 Modal 底部按钮的写法一致）。原因是 `flex-1` 的
 *   `flex-basis` 是 **0**：它和 `shrink-0` 的按钮一起放，`flex-wrap` **永远不会换行**，
 *   文字只能拿按钮吃剩下的宽度 ——「待家长验收」那块就是这样被挤成 8 字/行的（见
 *   `ApprovalActions`）。同理，**别给说明文字块 `shrink-0`**（`flex-basis: auto` 时
 *   按 max-content 算，窄屏会溢出卡片 —— 见「选日期」那张卡）。
 * - **没有新增任何 UI 库**：只用 `components/ui` 下既有的
 *   Button / Input / Select / Modal / Card / Toast / Bits。
 * - **可点行不加 hover 底色**（沿用目标页的做法）：历史清单的可点行只给 `cursor-pointer`
 *   与按下时的轻微变化，不加 `hover:bg-*`。
 * - 保存按钮**永远在视口底部可达**（`sticky bottom-0`），因为手机上录完一天的作业
 *   往往要往下滚很久。
 * - **少用的字段默认折叠**（摘要行是「时间：…」「更多：…」，开关是时钟 / 齿轮图标按钮）：
 *   家长一般只填"科目 + 作业项"，
 *   时刻与分值那六个输入框常驻会让手机上一张科目卡长到要滚三屏。
 *   ⚠️ 折叠的**只是输入框**：值一直在草稿里、`homeworkDraftToInput()` 照旧全部提交，
 *   提交路径上**没有任何"按可见性裁剪"的分支**（这正是本项目上一次数据丢失的形状）。
 *   折叠时用 `describeSubjectScheduleInput()` / `describeItemPointsInput()`
 *   把已有的值显示在那一行摘要里 —— 家长绝不会以为"这一项没设过"。
 *   折叠的默认状态**由值决定**：已有值 = 默认展开，由家长自己收起。
 * - **折叠区的开关按钮是图标**（时间 / 更多，以及复制 / 删除），
 *   都带 `aria-label` + `title`、点击区 ≥44px；**但摘要行（"时间：当天完成…"、
 *   "更多：0 分"）一个字都不能省** —— 它是"这里已经设过值"的唯一信号。
 * - **图标是 vendored 的 Lucide 矢量图标**（`components/ui/Icons.tsx`，ISC 许可），
 *   **不是 emoji**：emoji 是彩色字形、不认 `currentColor`（"删除"按钮的图标没法
 *   跟着危险色走），而且宽度随字形变。只有**图标控件**用矢量图标；
 *   页面里**别的 emoji（📚 今天的作业 / ⏰ 超时 / ✅ 全部完成 这类文案里的）不动**
 *   —— 不要因为"设计一致性"去改用户没抱怨的东西。
 */

// ================================================================ HELP_MOVED_TEXTS
//
// 📌 2026-10-14：按用户实测要求（"我红框圈出来的文字去掉，很啰嗦。这些以后可以统一放在
// **帮助**里面去。"），从这一页的界面上删掉了三段**背景解释 / 推销式说明**。
// 它们**不是被丢弃**，只是**还欠一个安放的地方** —— 未来的「帮助」页（⛔ 本轮**不做**，
// 不要顺手新建）。下面登记每一段的原文与去向，避免以后有人以为文案丢了、
// 又把它们原样贴回表单底部（那正是用户抱怨的东西）：
//
//   1.（选日期那张卡）「作业每天都不同，所以要按天录入；翻到过去的那一天就能看当时的记录。」
//      → 去向：帮助页「作业为什么按天录入」。
//        界面上现在只留标题「看哪一天的作业」——日期输入框自己会说话
//        （它还有 `aria-label="选择日期"`）。
//
//   2.（这天的概况卡）「这一天还没有作业。可以点「复制上次的结构」把上一次的科目与作业项
//      搬过来（不会复制完成状态），也可以直接录入。」
//      → 去向：帮助页「第一次录入」。界面上由 `CardHeader.description`
//        （「这一天还没有录入作业」）与卡片右上角那两个按钮（「复制上次的结构」/
//        「开始录入」）表达 —— 操作按钮本身就是更短的说明。
//
//   3.（录入区底部）「保存是整单覆盖：这一天的作业以你这次提交的为准。孩子已经打过卡的
//      作业项不会被删除，只会从计划里移出并保留记录（到时会有提示）。改名字等于"删掉旧的、
//      新建一个"。」
//      → 去向：**不删信息、只换时机**。保存前的移除确认框（`pendingRemovals` Modal）
//        已经把"这次保存会移走哪些科目/作业项""孩子打过卡的项不会被删除"逐条说清，
//        而那正是家长**需要**这句话的时刻。帮助页再放一份"整单覆盖"的完整解释。
//
// ⚠️ 反过来，**操作性的短提示一条都不能删**（它们不是"说明"，是"不知道就会填错的规则"）：
//   - 「留空 = 当天完成（不显示 23:59）」= `dueTimeSource === 'dayEnd'` 的用户侧口径；
//   - 「验收通过才入账；留空 = 不计分（新项默认 0 分）」= 计分规则；
//   - 「填了目标值就必须填单位」= 校验规则（而且它背后挂着"强制展开折叠区"的逻辑）；
//   - 删科/删项/清空当天/保存覆盖那几处**后果提示**（它们说的是"点下去会发生什么"）。
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  HOMEWORK_DEFAULT_POINTS_PER_UNIT,
  HOMEWORK_SKIP_CONSEQUENCE_TEXT,
  addDays,
  defaultHomeworkDueDate,
  describeHomeworkDueDefault,
  describeHomeworkDueTimeHint,
  describeHomeworkFormDrift,
  describeHomeworkRemovals,
  describeItemPointsInput,
  describeSkipResult,
  describeSubjectScheduleInput,
  emptyHomeworkItemDraft,
  emptyHomeworkSubjectDraft,
  formatDuration,
  formatOverdueDuration,
  hasHomeworkFormDrift,
  hasHomeworkRemovals,
  homeworkCopyGate,
  homeworkDayToDraft,
  homeworkDraftToInput,
  homeworkDueDateLabel,
  homeworkDueQuickOptions,
  homeworkDueSuggestion,
  homeworkFormDrift,
  homeworkFormSnapshot,
  homeworkOverdueSeconds,
  homeworkPlanRemovals,
  dedupeHomeworkQueueEntries,
  itemExtraIsSet,
  newHomeworkDraftKey,
  normalizeHomeworkSubjectName,
  subjectScheduleIsSet,
  todayLocal,
  validateHomeworkDraft,
  type HomeworkCopyGate,
  type HomeworkDraftItem,
  type HomeworkDraftProblem,
  type HomeworkDraftSubject,
  type HomeworkRemovalPreview,
} from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconCopy,
  IconSettings,
  IconTrash,
} from '../../components/ui/Icons';
import { Modal } from '../../components/ui/Modal';
import { ApprovalActions } from '../../components/homework/ApprovalActions';
import { OverdueRow } from '../../components/homework/OverdueRow';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, iconForName, statusMeta } from '../../components/ui/Bits';
import {
  useHomeworkCheckinMutations,
  useHomeworkDay,
  useHomeworkHistory,
  useHomeworkMutations,
  useHomeworkOpen,
  useHomeworkOverdue,
} from '../../lib/domainQueries';
import {
  useHomeworkApprovalActions,
  type HomeworkApprovalActionTarget,
} from '../../lib/homeworkApprovalActions';
import { useStudent } from '../../lib/queries';
import { useParentActor } from '../../lib/ActorContext';
import { canCreate } from '../../lib/uiMode';
import { homeworkDraftSyncDecision } from '../../lib/homeworkDraftSync';
import {
  HOMEWORK_SUBJECT_NEW_OPTION,
  HOMEWORK_SUBJECT_PLACEHOLDER,
  homeworkSubjectFieldState,
  subjectSuggestionsOf,
} from '../../lib/homeworkSubjects';
import { newIdempotencyKey } from '../../lib/api/client';
import { toErrorMessage } from '../../lib/api/error';
// 「恢复成未完成」的文案与学生端共用一份（见 lib/skipUndo.ts）：
// 两端说法必须一致，否则家长看到的"撤销"和孩子看到的"恢复"会被当成两件事。
import { RESTORE_ENTRY } from '../../lib/skipUndo';
import {
  describeSubjectSchedule,
  isHomeworkItemEmpty,
  type HomeworkDayResult,
  type HomeworkHistorySheet,
  type HomeworkItemView,
  type HomeworkOverdueEntry,
} from '../../lib/api/homework';
import { cn } from '../../lib/utils';

// ================================================================ 草稿模型
//
// 草稿的形状、校验、"草稿 → 整单保存入参"的规则、以及**"打开「修改」时草稿里有什么"**
// 这四件事**都不在这里**：它们是纯函数，住在 `@fht/shared` 的 `homework-draft.ts` 里，
// 由 `packages/shared/src/__tests__/homework-draft-view.test.ts`（纯规则）与
// `apps/api/src/__tests__/homework.draft.test.ts`（payload 级回归）测试同一份实现
// （这里只负责 React 状态与界面）。
//
// ⚠️ 为什么连"读侧 → 草稿"也必须住在 shared（2026-10-13 的生产事故）：
// 旧写法把 `subjectToDraft()` 写在这个文件里，并在"整科都是保留项"时返回 `null`
// —— 于是那一科**不进表单**，而"保存 = 整单覆盖"意味着"表单里没有的 = 从这天移除"。
// 用户"只是加了一科"，另一科就被从当天的计划里移除了。
// 现在那份判定是纯函数 `homeworkDayToDraft()`，它的不变式被测试直接锁住：
// **这一天仍然有效的科目与作业项，一个都不会缺席**。
//
// 页面这边只剩两件本地的事：
//   1. 把服务端视图（`HomeworkSubjectView` 等）喂给 `homeworkDayToDraft()` —— 结构完全一致，
//      所以不需要任何转换（`dueTimeInputOf` 也已由 shared 提供，读侧/写侧共用一份）；
//   2. 保存前的**语义护栏**：`homeworkPlanRemovals()` + 一次明确确认（见 `doSave()`）。
//
// ⚠️ 2026-10-16：`dayToDraft()` 必须拿到**这一天的日期**（`planDate`）。
// 新建科目卡的截止日期由默认策略算（周五/周六布置 → 周日，见 `homework-due.ts`），
// 而"布置日"只有页面知道；不传它就只能退化成"与布置日同一天"。

function dayToDraft(
  day: HomeworkDayResult | undefined,
  planDate: string,
): HomeworkDraftSubject[] {
  return homeworkDayToDraft(day, planDate);
}

/**
 * 这一天里"仍在计划中的科目"各自有哪些项已被移出计划（保留记录）——
 * 录入区的说明文字要用它。
 *
 * 整科 `retained` 的科目不在结果里：它们已经不在计划里，页面在
 * 「已移出计划的科目（保留记录）」区块单独展示，也不该出现在录入区。
 */
function retainedItemNamesOf(day: HomeworkDayResult | undefined): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const subject of day?.subjects ?? []) {
    if (subject.retained) continue;
    const names = subject.items.filter((item) => item.retained).map((item) => item.name);
    if (names.length > 0) map.set(subject.name, names);
  }
  return map;
}

// ================================================================ 小展示件

/**
 * 跨天队列（「超时」与「还有效」）里的一行。
 *
 * `HomeworkOpenEntry` 是 `HomeworkOverdueEntry` 的超集（只多两个字段），
 * 所以两条队列共用 `OverdueSection` 时用这个结构类型即可 ——
 * `badge` 是可选的：只有「还有效」那一支才有（服务端算好的逐字文案）。
 */
type HomeworkQueueEntry = HomeworkOverdueEntry & { badge?: string };

/**
 * 作业项的**当前状态**（只读展示）。
 *
 * ⚠️ 判据是"服务端给的 `status` / `checkin`"，**不是**"这个名字是不是新出现的"：
 * 同名重新加回来会复用原记录，所以新出现的名字也可能带着昨天的打卡记录
 * （见 `isHomeworkItemEmpty()`）。状态文案与图标统一走 `statusMeta`。
 */
function ItemStatusLine({ item }: { item: HomeworkItemView }) {
  const meta = statusMeta(item.status);
  const checkin = item.checkin;
  const abandoned = item.status === 'skipped';

  const details: string[] = [];
  // 「实际用时」与「超时多久」是两件事，各有唯一出处（都在 @fht/shared）：
  //   - 用时 → formatDuration（'45 秒' / '19 分 23 秒'）—— 用户的真实投入，永不丢秒
  //   - 超时 → formatOverdueDuration + homeworkOverdueSeconds（'25 分钟' / '不到 1 分钟'）
  // ⛔ 不要在这里自己写 `> 0` 或自己拼分钟：那正是"45 秒显示成 0 分钟"的老路。
  if (checkin && checkin.actualSeconds > 0) {
    details.push(`用时 ${formatDuration(checkin.actualSeconds)}`);
  }
  // 判据也走共享的 `homeworkOverdueSeconds()`（与两端共用的超时行同一个出口）：
  // 它排掉 done/skipped/missed，避免"已完成 + 超时 3 小时"这种自相矛盾的一行
  // （缓存里 overdueSeconds 是请求那一刻的静态值，status 才反映最新事实）。
  const overdueSeconds = homeworkOverdueSeconds(checkin);
  if (overdueSeconds !== null) {
    details.push(`超时 ${formatOverdueDuration(overdueSeconds)}`);
  }
  if (checkin?.skipReason) details.push(`放弃原因：${checkin.skipReason}`);
  if (checkin?.note) details.push(checkin.note);
  if (item.status === 'submitted') details.push('已提交，等你验收');

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <Badge tone={meta.tone}>
        {meta.icon} {meta.label}
      </Badge>
      {item.pointsPerUnit > 0 && (
        <span className="text-slate-500">完成可得 {item.pointsPerUnit} 分</span>
      )}
      {/* 0 分必须是**看得见**的：否则家长点「通过」之后会觉得"怎么没加分"是坏了 */}
      {item.pointsPerUnit <= 0 && <span className="text-slate-400">不计分</span>}
      {item.targetValue !== null && (
        <span className="text-slate-500">
          目标 {item.targetValue}
          {item.unit ?? ''}
        </span>
      )}
      {abandoned && <span className="text-slate-400">这一项不再计入完成</span>}
      {details.length > 0 && <span className="text-slate-400">{details.join(' · ')}</span>}
    </div>
  );
}

/**
 * 一条作业项的**验收操作**（仅家长，且仅 `submitted` 时渲染）。
 *
 * 📌 2026-10-15：这一段原来是**这个文件里的局部组件**，于是它只可能出现在
 * 「今天的作业」的查看态里 —— 而「超时」是跨天的派生视图，昨天提交的作业项
 * **只出现在那里**，家长因此"想点通过却没有按钮"（用户实测原话）。
 * 现在它搬到了 `components/homework/ApprovalActions.tsx`，
 * 「今天的作业」的作业项与「超时」行渲染的是**同一个组件**；
 * 动作与缓存失效则共用 `lib/homeworkApprovalActions.tsx` 的
 * `useHomeworkApprovalActions()`。⛔ 不要在这里再写回一份。
 *
 * 显示条件仍然是**服务端**给的 `canApprove`（= `canApproveCheckin(actor)`，
 * 家长 owner/editor 为 true），前端不自己判角色 —— 判定只有一份实现。
 *
 * 两个动作的语义差别（服务端也是这么实现的）：
 *  - **通过** → `submitted → done`，**此刻才计分**；重复点击幂等，不会重复加分；
 *  - **打回** → `submitted → pending`，**不计分**，孩子要重做；
 *    可填原因，原因会同时进事件流与 `checkin.note`（孩子端卡片上直接能看到）。
 */
function itemApprovalTarget(item: HomeworkItemView, date: string): HomeworkApprovalActionTarget {
  return {
    /**
     * ⚠️ 作业项的验收认的是**打卡记录**的 id（`item.checkin.checkinId`），
     * 不是 `habitId`。「超时」队列那边则是条目自己的 `id`（它就是 checkinId）
     * —— 两者最终都交给同一套端点与同一个 hook。
     */
    checkinId: item.checkin?.checkinId ?? '',
    name: item.name,
    pointsPerUnit: item.pointsPerUnit,
    // 弹窗里那行"哪一天的作业"（见 `HomeworkApprovalActionTarget.date`）
    date,
  };
}

/** 「已移除（保留记录）」的说明条：**必须让家长知道它不会出现在孩子端**。 */
function RetainedNote({ className }: { className?: string }) {
  return (
    <p className={cn('mt-2 rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-600', className)}>
      🗄️ 已移出这天的作业计划，但<strong>保留了孩子的打卡记录</strong>（记录与积分依据不能删）。
      它<strong>不会</strong>出现在孩子端的今日清单里；想重新布置，就在下面把同名科目 / 作业项加回来
      —— 同名会接着用原来的记录，不会变成一项空白作业。
    </p>
  );
}

// ================================================================ 折叠区的开关（隐藏的是 UI，不是数据）
//
// 用户的要求："「开始时刻」「截止时刻」「时长」默认隐藏，点某个按钮才显示；
// 「分值」「目标值」「单位」也是。这样整个界面就简洁很多。"
//
// ⚠️ 这里唯一危险的东西是**"隐藏 ≠ 没有值"**。本项目刚发生过一次同形状的生产事故：
// 家长打开「修改」→ 表单里**没显示**已有的科目 → 保存时 payload 里没有它
// → 那一科被从当天的计划里移除。同一类陷阱在折叠字段上更危险：折叠起来的时刻与分值
// 如果既不显示、也不提交，家长点一次保存就会把**他从没见过的值**悄悄抹掉。
//
// 因此折叠这件事被拆成两条**成对**的保证，改任何一条都要同时看另一条：
//   1. **数据侧**：折叠值一直待在草稿里，`homeworkDraftToInput()` 原样提交
//      （回归测试：`apps/api/src/__tests__/homework.draft.test.ts` 的「折叠字段」一节）；
//   2. **界面侧**：折叠着的时候，摘要行必须把已有的值显示出来
//      （`describeSubjectScheduleInput()` / `describeItemPointsInput()`），
//      而且**已经填过值的折叠区默认是展开的**（`subjectScheduleIsSet()` / `itemExtraIsSet()`）
//      —— 家长自己收起是允许的，但那是一次明确的选择。
//
// 折叠状态刻意是**每个科目卡 / 每个作业项自己的**局部 state（不是页面的 state）：
// 它只影响"这一张卡看起来多长"，与保存、校验、漂移护栏全部无关。
// 卡片由 `key={subject.key}` / `key={item.key}` 标识，草稿被服务端结果重建时 key 会变
// → 组件重挂载 → 折叠状态重新按最新的值推导，不会出现"新的一科却继承了上一科的折叠状态"。

/**
 * 把「填了目标值就必须填单位」这条错误变成一个**强制展开「更多」**的开关。
 *
 * 为什么需要它：那条错误指向的输入框（「单位」）在折叠区里。错误提示看得见、
 * 该改的地方看不见，家长只会得出"保存不了"的结论 —— 而这正是用户报过的那类故障。
 *
 * ⚠️ 键用**错误文案**、而不是 `problems` 数组本身：每次校验都会产生一个新数组，
 * 用数组/引用做键会让"用户刚收起就被重新弹开"（`useDisclosure` 里按值比较）。
 * 返回空串 = 没有这类错误 = 不干预用户的折叠选择。
 */
function unitProblemForceOpenKey(problems: readonly HomeworkDraftProblem[]): string {
  return problems
    .filter((problem) => problem.message.includes('填了目标值就必须填单位'))
    .map((problem) => problem.message)
    .join('|');
}

/**
 * 局部的展开 / 收起状态。
 *
 * `initiallyOpen` 由**值**决定（见上面的注释），之后完全交给用户；
 * `forceOpenKey` 是"有必须被看见的校验错误"时的一次性强制展开
 * （例如「填了目标值就必须填单位」—— 那条错误指向的正是一个折叠着的输入框，
 * 错误提示看得见而输入框看不见，家长只会觉得"保存不了"）。
 */
function useDisclosure(initiallyOpen: boolean, forceOpenKey = '') {
  const [open, setOpen] = useState(initiallyOpen);
  const lastForceKeyRef = useRef('');
  useEffect(() => {
    if (forceOpenKey === '' || forceOpenKey === lastForceKeyRef.current) return;
    lastForceKeyRef.current = forceOpenKey;
    setOpen(true);
  }, [forceOpenKey]);
  return { open, setOpen };
}

/**
 * 这一页里**唯一**的"图标控件"实现。
 *
 * ## 它修的是什么（2026-10-14 用户实测）
 *
 * 「这些控件能不能是同一样式的？每个都是不一样的样式，很难看。」
 * 「控件没有水平对齐 / 没有垂直对齐。」
 *
 * 同一个"图标按钮"，四个调用点各写各的，于是同一屏里出现了四五种外观：
 *
 * | 原来的调用点 | 变体 | 尺寸 | 实测结果（390px） |
 * | --- | --- | --- | --- |
 * | ↑ ↓ | `secondary` | `md`（`h-11 px-4`） | 白底有框，宽 **42px** |
 * | 📋 | `secondary` + 里面套 `<span>` | `md` | 白底有框，宽 **55.97px** |
 * | 🗑（删科） | `danger` | `md` | **红底、没有边框**，宽 **43.19px** |
 * | 🗑（删项） / 🕐 / ⚙️ | `ghost` | `md` | **透明、无边框**（= 用户眼里的"裸 emoji"），宽 43.19 / 53.97px |
 *
 * 高度都是 44px（`size="md"` 从上一轮起就统一了），**宽度却跟着字形走**：
 * 42 / 43.19 / 53.97 / 55.97 四种；再加上"有没有边框、白底还是红底"，
 * 横看竖看都不齐 —— 这就是用户看到的"每个都不一样"。
 *
 * ## 现在的口径（三条，缺一不可）
 *
 * 1. **尺寸**：`size="icon"` = `h-11 w-11` = **44×44**（手机点击区达标），
 *    与旁边输入框/下拉框的 `h-11` **等高**（`items-end` 对齐时底边自然齐）。
 * 2. **外观**：默认 `variant="secondary"`（白底 + 1px 边框 + 圆角）。
 *    **只有"删除"允许用 `variant="danger"`（红）**，但尺寸、形状、边框宽度与其它完全一致
 *    —— 所以 danger 的调用点必须补 `border border-rose-600`：`danger` 变体自己
 *    **不带边框**，不补这一句，红的那个就"形状不一样"。
 * 3. **图标**：一律是 `components/ui/Icons.tsx` 里 vendored 的 Lucide 线条图标
 *    （`size={20}`，`stroke="currentColor"`），**不再用 emoji**。
 *    2026-10-14 用户实测："那些按钮 icon 是矢量图标吗？怎么和原来的一样？没变化呀！"
 *    —— 他说得对：上一轮只统一了外框尺寸，图标本身还是 🕐 ⚙️ 📋 🗑 ↑ ↓ ← →
 *    这些**彩色字形**，宽度随字形走、而且**不认 `currentColor`**
 *    （"删除"按钮的图标没法跟着危险色走）。所以这一轮把图标本身换成了矢量图标。
 *    同一个图标集的 24×24 网格 + 同一条 `stroke-width` + 同一个圆角，
 *    8 个图标的视觉重量天然一致。详见 `Icons.tsx` 文件头。
 *
 * ## 两条不许动的约定
 *
 * - `aria-label` + `title` **一个都不能省**（手机上图标没有 hover 提示，读屏只能靠
 *   `aria-label`）—— 所以 `label` 是必填参数，不是可选项。
 * - 收起/展开的状态**不再用 `variant` 区分**（`secondary` / `ghost` 混用正是用户抱怨的
 *   "每个都不一样"）：状态由 `aria-expanded` + "下面那块看得见的输入区"表达。
 */
function IconAction({
  label,
  icon,
  danger = false,
  disabled,
  expanded,
  onClick,
}: {
  /** 可访问名字（同时用作 `aria-label` 与 `title`）。必填。 */
  label: string;
  /**
   * 图标本身。**必须**是 `components/ui/Icons.tsx` 里的矢量图标组件
   * （`<IconChevronLeft />` 这类，`size` 默认 20）。
   * 这里刻意收 `ReactNode` 而不是字符串：传字符串就等于允许 emoji 再溜回来。
   */
  icon: ReactNode;
  /** 只有"删除"类动作用危险色；尺寸与形状不变。 */
  danger?: boolean;
  disabled?: boolean;
  /** 折叠开关传：读屏据此播报展开状态。 */
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="icon"
      variant={danger ? 'danger' : 'secondary'}
      className={danger ? 'border border-rose-600' : undefined}
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      disabled={disabled}
      onClick={onClick}
    >
      {/* 图标自己带 `aria-hidden`（见 Icons.tsx 的底座）：语义走按钮的 aria-label。 */}
      {icon}
    </Button>
  );
}

/**
 * 折叠区的一行：摘要 + 展开/收起按钮（窄屏上摘要与按钮**必须**能换行）。
 *
 * ## 为什么开关是**图标按钮**而不是「设置开始 / 截止时刻」这种文字按钮
 *
 * 2026-10-14 用户实测："文字按钮改成图标按钮"。原来那个文字按钮把一整句话塞进卡片，
 * 手机上每个折叠区都多占一截；换成图标后摘要行只剩"值"这一件事。
 *
 * ⚠️ 但**摘要那一行一个字都不能省**（`{label}：{summary}`）：
 * 它是"这里已经设过值"的**唯一信号**（「时间：当天完成（不显示 23:59）」「更多：0 分」）。
 * 本项目刚因为"隐藏的值看不见 / 被丢掉"出过事故，改按钮样式时不要顺手把摘要也删掉。
 *
 * ⚠️ 图标按钮的可访问性：`aria-label` + `title` 都要有（手机上图标没有 hover 提示，
 * 但 title 对桌面/辅助技术仍然有用），且点击区 **≥44px**（`size="icon"` 的 `h-11 w-11`）。
 * 图标是 `components/ui/Icons.tsx` 里 vendored 的 Lucide 矢量图标（原来是 🕐 / ⚙️ 两个 emoji）。
 */
function FoldToggle({
  label,
  summary,
  open,
  onToggle,
  icon,
  actionLabel,
  collapseLabel,
}: {
  label: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  /** 展开/收起这个折叠区的图标（`components/ui/Icons.tsx` 里的矢量图标）。 */
  icon: ReactNode;
  /** 收起状态下这个按钮的名字（= 点它会做什么），同时用作 `title`。 */
  actionLabel: string;
  /** 展开状态下这个按钮的名字（"收起…"）。 */
  collapseLabel: string;
}) {
  const buttonLabel = open ? collapseLabel : actionLabel;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {/*
        ⚠️ 摘要块是 `min-w-0 flex-1`（可伸缩那一列），按钮是 `shrink-0`：
        既有约定就是这个方向（见文件头的「界面取舍」）。
        反过来写（给摘要 `shrink-0`）会让它在窄屏按 max-content 撑开、溢出卡片。
      */}
      <span className="min-w-0 flex-1 text-xs text-slate-500">
        {label}：{summary}
      </span>
      {/*
        ⚠️ 开关用 `IconAction`（本页唯一的图标控件实现，见它的注释）：
        以前这里是 `variant={open ? 'secondary' : 'ghost'}` —— 收起时是**透明无边框**的
        "裸 emoji"，正是用户截图里「🕐 在最右边、跟别的控件不在一条线上」的那一个。
        现在收起与展开**外观完全一样**（白底 + 边框 + 44×44），状态走 `aria-expanded`。
      */}
      <IconAction label={buttonLabel} icon={icon} expanded={open} onClick={onToggle} />
    </div>
  );
}

/**
 * 一个作业项的录入（名称 + 折叠起来的「分值 / 目标值 / 单位」）。
 *
 * ⚠️ 「更多」折叠的默认状态：**已经填过值就默认展开**（`itemExtraIsSet()`）。
 * 分值为 0 的新项默认**收起** —— 用户明确说「作业目前我是不计分的」，
 * 所以"分值是 0、没有目标值"的新项不该再占三行屏幕。
 * 但摘要行会明确写出「更多：0 分」，不会让人以为"这里没设置过"
 * （0 分是**常态**：`HOMEWORK_DEFAULT_POINTS_PER_UNIT = 0`，所以它必须看得见，
 * 不能被当成"没设置"而藏起来）。
 */
function ItemEditor({
  item,
  index,
  forcedMoreKey,
  onPatch,
  onRemove,
}: {
  item: HomeworkDraftItem;
  index: number;
  /** 非空 = 这一项有必须被看见的校验错误 → 「更多」强制展开。 */
  forcedMoreKey: string;
  onPatch: (patch: Partial<HomeworkDraftItem>) => void;
  onRemove: () => void;
}) {
  const more = useDisclosure(itemExtraIsSet(item), forcedMoreKey);
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <Input
            label={`作业 ${index + 1}`}
            placeholder="例如 抄写生字 3 遍"
            value={item.name}
            maxLength={64}
            onChange={(e) => onPatch({ name: e.target.value })}
          />
        </div>
        {/*
          「删掉」→ 🗑 图标按钮（用户要求文字按钮图标化）。
          危险动作仍然靠**语义**交代：`aria-label` + `title` = 「删掉这项作业」。
          误触的代价是有界的：这一步只改本地草稿，真正落库要再点「保存」，
          而保存前那道移除确认框会把"要移走什么"逐条列出来（见 `pendingRemovals`）。

          2026-10-14 用户实测这一格是"裸的小 emoji"：它当时用 `variant="ghost"`
          （透明、无边框）。现在走 `IconAction` 的 `danger` 支路 ——
          与同页其它图标控件**同尺寸（44×44）、同形状（1px 边框、同圆角）**，只有颜色是红的。
          与前一行输入框：`items-end` 底对齐，输入框 `h-11` = 44px，两者**等高**（实测顶边/底边逐像素相同）。
        */}
        <IconAction label="删掉这项作业" icon={<IconTrash />} danger onClick={onRemove} />
      </div>

      <div className="mt-2 space-y-2">
        <FoldToggle
          label="更多"
          summary={describeItemPointsInput(item)}
          open={more.open}
          onToggle={() => more.setOpen(!more.open)}
          icon={<IconSettings />}
          actionLabel="设置分值 / 目标值"
          collapseLabel="收起分值 / 目标值设置"
        />
        {more.open && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Input
              label="分值（可选）"
              type="number"
              min={0}
              max={1000}
              inputMode="numeric"
              value={item.pointsPerUnit}
              onChange={(e) => onPatch({ pointsPerUnit: e.target.value })}
              hint={`验收通过才入账；留空 = 不计分（新项默认 ${HOMEWORK_DEFAULT_POINTS_PER_UNIT} 分）`}
            />
            <Input
              label="目标值（可选）"
              type="number"
              min={1}
              inputMode="decimal"
              value={item.targetValue}
              onChange={(e) => onPatch({ targetValue: e.target.value })}
            />
            <Input
              label="单位（可选）"
              maxLength={8}
              placeholder="遍 / 页 / 题"
              value={item.unit}
              onChange={(e) => onPatch({ unit: e.target.value })}
              hint="填了目标值就必须填单位"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 一个科目的录入卡：科目名（**下拉选以前录过的** + 可继续手输新科目）、
 * 折叠起来的「⏱ 时间」、以及若干作业项。
 *
 * ## 科目为什么是「下拉 + 输入新的」
 *
 * 目标只有一个：**不用每次都手输**。所以下拉的候选 = 这一天已有的科目 + 历史作业里
 * 录过的科目（见 `lib/homeworkSubjects.ts`），而"新科目"永远能填
 * （新增科目是这个页面的主路径之一，下拉不能变成一道墙）。
 *
 * ## 时间为什么默认收起
 *
 * 家长一般只填科目与作业项，时刻那三格常驻会让一张卡在手机上长到要滚很久。
 * 收起 ≠ 不提交：值一直在草稿里（见文件头「折叠字段」的注释）。
 */
function SubjectEditor({
  subject,
  index,
  subjectCount,
  planDate,
  suggestions,
  problems,
  forcedMoreKey,
  retainedNames,
  onPatchSubject,
  onMove,
  onDuplicate,
  onRemove,
  onAddItem,
  onPatchItem,
  onRemoveItem,
}: {
  subject: HomeworkDraftSubject;
  index: number;
  subjectCount: number;
  /**
   * **这一天的日期**（作业单的 `start_date`）。
   *
   * 它是"默认截止日期"的输入（周五/周六布置 → 周日），也是日期框的 `min`
   * —— 截止日不能早于布置日（服务端同样会拒）。
   */
  planDate: string;
  /** 以前录过的科目名（去重、最近的在前）—— 下拉候选。 */
  suggestions: readonly string[];
  problems: HomeworkDraftProblem[];
  /** 非空 = 这一科有必须被看见的校验错误 → 「更多」强制展开。 */
  forcedMoreKey: string;
  retainedNames: readonly string[];
  onPatchSubject: (patch: Partial<HomeworkDraftSubject>) => void;
  onMove: (delta: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onAddItem: () => void;
  onPatchItem: (itemKey: string, patch: Partial<HomeworkDraftItem>) => void;
  onRemoveItem: (itemKey: string) => void;
}) {
  const schedule = useDisclosure(subjectScheduleIsSet(subject));

  /**
   * 截止日期的**有效值**与它的两个参照点。
   *
   * `subject.dueDate` 在正常路径下一定有值（草稿构建时由默认策略/服务端回填填好），
   * 这里再兜一次"空 → 布置日"，是为了让**界面显示的**与**提交的**永远一致：
   * `homeworkDraftToInput()` 把空串解释成"与布置日同一天"，
   * 所以这里的兜底也必须是布置日，**不能**是"默认策略算出来的周日"
   * （否则会出现"界面写着周日、存下去是当天"这种最难查的错）。
   */
  const effectiveDueDate = subject.dueDate.trim() === '' ? planDate : subject.dueDate.trim();
  const defaultDueDate = defaultHomeworkDueDate(planDate);
  const isDefaultDueDate = effectiveDueDate === defaultDueDate;
  const dueLabel = homeworkDueDateLabel(effectiveDueDate, planDate);
  const dueQuickOptions = homeworkDueQuickOptions(planDate);
  /** 方案丙（默认仍是当天）时给出的"建议留到周末"提示；当前策略下恒为 null。 */
  const dueSuggestion = homeworkDueSuggestion(planDate);
  /**
   * 科目那一格是"下拉"还是"输入框"，由**纯函数** `homeworkSubjectFieldState()` 决定
   * （`lib/homeworkSubjects.ts`，回归测试在 `homeworkSubjects.test.ts`）。
   *
   * ⚠️⚠️ 2026-10-14 用户实测报的「**科目下拉没有生效**」就修在这里。旧写法：
   *
   * ```ts
   * useState(() => name === '' || !suggestions.includes(name))  // ← bug
   * ```
   *
   * 名字为空（**新增科目行**）当然不在候选里 → 永远落进"输入框"那一支，
   * 而下拉候选（该学生的历史科目）**只在这一个开关为 false 时才渲染**，
   * 于是家长永远看不到下拉、只能每次手输 —— 而"新增一科"正是这个页面的主路径
   * （这一天还没录作业时，表单里唯一那一行就是空名字的新行）。
   * 更糟的是它只在挂载时算一次：候选是**异步**回来的，回来之后也不会切回下拉。
   *
   * 现在只剩"用户显式点了「＋ 输入新的科目…」"这一个本地开关，
   * 其余全部由名字与候选推导 —— 空名字 = 下拉（第一眼就能选），
   * 名字在候选里 = 下拉且选中项看得见，名字不在候选里 = 仍下拉并把当前名字补成选项
   * （所以不会出现"下拉看起来没选中任何项"）。
   */
  const [newSubjectMode, setNewSubjectMode] = useState(false);
  /**
   * 「科目」这一格提示语的 id。
   *
   * 提示语从 `Select` 的 `hint` 搬到了整行下面（原因见下面那一行的注释），
   * 所以要自己给一个 id、再用 `aria-describedby` 接回去 —— 读屏仍然能把
   * "从以前录过的科目里选…"这句话当成这一格控件的描述念出来。
   */
  const hintId = useId();
  const field = homeworkSubjectFieldState({
    name: subject.name,
    suggestions,
    newSubjectMode,
  });

  /** 下拉里选中「＋ 输入新的科目…」：进输入框（新增科目这条路不能被堵死）。 */
  function enterNewSubjectMode(): void {
    const normalized = normalizeHomeworkSubjectName(subject.name);
    /*
      ⚠️ 名字清不清空，分两种情况（两种都必须是**有意的**）：
        - 名字来自候选（家长刚在下拉里选的）：**清空** —— 否则旧名字留在框里像"没生效"；
        - 名字**不在**候选里（服务端回填的自定义科名，或家长手输到一半的名字）：
          **必须留着** —— 清掉等于让家长重打一遍，而这一页最忌讳的就是"值看不见/丢了"。
    */
    const fromSuggestions =
      suggestions.includes(subject.name) || (normalized !== '' && suggestions.includes(normalized));
    setNewSubjectMode(true);
    if (fromSuggestions) onPatchSubject({ name: '' });
  }

  return (
    <Card className={cn(problems.length > 0 && 'border-rose-300')}>
      {/*
        科目头：名称 + 行内动作。

        ⚠️ 2026-10-14 用户实测的两件事都修在这一行里：

        1.「控件没有垂直对齐」—— 根因是**提示语原本挂在 `Input/Select` 的 `hint` 上**，
           而这一行是 `items-end`：提示语在 320/390px 会被挤成 2~3 行，FieldShell 因此
           从「label + 控件 = 70px」长到一百多像素，按钮的底边就跟着提示语的底边跑
           —— 实测按钮顶边比下拉框顶边**低了 70px**（下拉 289~333，按钮 359~403）。
           修法：提示语**从字段里搬出来**，落到整行下面（见这一行后面的两个 `<p>`）。
           现在 FieldShell 只剩 label + 控件，按钮 `items-end` 与控件**逐像素等高对齐**
           （与作业项那一行完全同一种结构）。

        2.「每个控件都是不一样的样式」—— 四个按钮统一走 `IconAction`
           （44×44、同变体、同边框、图标各自锁字号），见它的注释。

        两条既有约定**照守、不许动**：可伸缩的那一列是 `min-w-0 flex-1`，
        按钮列 `shrink-0`（`size="icon"` 自带）—— 反过来写就会出现"文字被挤成一列"
        或"整页横向滚动"（本项目刚修过的那两个形状）。
      */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          {field.mode === 'input' ? (
            <Input
              label={`科目 ${index + 1}`}
              placeholder="例如 语文 / 数学 / 英语"
              value={subject.name}
              maxLength={64}
              /* 是家长自己点的「输入新的科目…」才会走到这里 → 直接聚焦、少一次点按。
                 新科目行不会自动落到这一支（见上面的注释），所以不会一进页面就弹键盘。 */
              autoFocus
              onChange={(e) => onPatchSubject({ name: e.target.value })}
            />
          ) : (
            <Select
              label={`科目 ${index + 1}`}
              /* `field.value` 的不变式：要么是 ''（占位项），要么一定在 options 里
                 —— 原生 select 找不到对应 option 时会显示成"没选中"，那正是用户报过的观感。 */
              value={field.value}
              onChange={(e) => {
                if (e.target.value === HOMEWORK_SUBJECT_NEW_OPTION) {
                  enterNewSubjectMode();
                  return;
                }
                onPatchSubject({ name: e.target.value });
              }}
              /* 提示语搬到了行下面（原因见这一行开头的注释）；
                 同时手工把可访问的"描述"接回去 —— `Select` 里 `{...rest}` 是最后展开的，
                 所以这里能覆盖组件内部那份由 `hint` 推导出来的 `aria-describedby`。 */
              aria-describedby={hintId}
            >
              {/* 占位项：新行还没选时说清"还没选"，而不是留一个空白控件让人猜。 */}
              <option value="" disabled>
                {HOMEWORK_SUBJECT_PLACEHOLDER}
              </option>
              {field.options.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              <option value={HOMEWORK_SUBJECT_NEW_OPTION}>＋ 输入新的科目…</option>
            </Select>
          )}
        </div>
        {/*
          ⚠️ 动作列用 `flex-wrap` + `ml-auto`：
          - `flex-wrap`：窄屏上这四个按钮（上移/下移/复制/删除）自己可以换行，不会把科目下拉压到看不见；
          - `ml-auto`：万一真的换到第二行，它仍然**贴右**（右边缘 = 卡片内容区右边缘），
            与下面两行（时间 / 更多）的图标保持在**同一竖直列**上 —— 这是"水平对齐"那条要求。
          既有约定照守：可伸缩的那一列（科目名）是 `min-w-0 flex-1`，按钮列不参与收缩
          （`size="icon"` 自带 `shrink-0`）。

          四个按钮统一 `size="icon"`（44×44）：点击区达标（手机优先）、
          与下拉框 `h-11` 等高，彼此也一样宽 —— 用户实测的"高度/宽度/垂直位置都不一样"就修在这里。
          名字走 `aria-label` + `title`，图标一律是 `components/ui/Icons.tsx` 里的矢量图标。
        */}
        <div className="ml-auto flex flex-wrap gap-1">
          <IconAction
            label="上移这个科目"
            icon={<IconArrowUp />}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          />
          <IconAction
            label="下移这个科目"
            icon={<IconArrowDown />}
            disabled={index === subjectCount - 1}
            onClick={() => onMove(1)}
          />
          {/*
            「复制」「删除」→ 图标。
            危险的「删除」**只留图标**的理由：这一步只改本地草稿（真正落库要再点「保存」，
            保存前那道移除确认框会把要移走的内容逐条列出来），误触在保存前都能挽回；
            而它用危险色 + 垃圾桶这个通用符号 + `aria-label`/`title`，
            "这是删除"这件事没有被藏起来 —— 换来的是这一行在 320px 手机上不再挤成两行。
          */}
          <IconAction label="复制这个科目" icon={<IconCopy />} onClick={onDuplicate} />
          <IconAction label="删除这个科目" icon={<IconTrash />} danger onClick={onRemove} />
        </div>
      </div>

      {/*
        两条提示语从字段里搬到了整行下面（原因见这一行开头的注释）。
        ⚠️ 内容一个字都没改、也没被藏起来；只是换了个位置。
        间距 `mt-1.5` 与原来 `FieldShell` 里的 `space-y-1.5` 是同一个 6px。
      */}
      {field.mode === 'input' ? (
        /* 回下拉的出口：没有它，「＋ 输入新的科目…」就是一条**出不来**的路。 */
        suggestions.length > 0 && (
          <p className="mt-1.5 text-xs text-slate-500">
            <button
              type="button"
              className="font-medium text-brand-700 underline"
              onClick={() => setNewSubjectMode(false)}
            >
              从列表选
            </button>
          </p>
        )
      ) : (
        <p id={hintId} className="mt-1.5 text-xs text-slate-500">
          从以前录过的科目里选；没有的就选「＋ 输入新的科目…」自己填
        </p>
      )}

      {/*
        ================================================================
        📅 截止日期（**不进折叠**）
        ================================================================

        用户报的问题就是"周五布置的作业周六就显示超时"，所以这一行必须**一眼看见**：
        默认值（周五/周六 → 周日）直接填进日期框里，并且下面用一句人话解释它是怎么来的
        —— 不是留空 + 一句提示，也不是藏在「时间」折叠区里。

        手机上好不好用（形态选择）：
          - 主控件是 `<input type="date">`：手机上点一下就是系统日历，任意日期都能选，
            而且**显示的永远是具体日期**（"截止到哪天"不靠猜）；
          - 上面一排快捷按钮（`homeworkDueQuickOptions()`）：`当天` / `默认 周日 10-18` /
            `明天`，点一下就换，不用打开日历。快捷项按值去重，绝不会出现两个按钮指向同一天；
          - `min={planDate}` 让系统日历直接挡住"选到布置日之前"（服务端也会拒）。
      */}
      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-slate-700">📅 截止日期</span>
          <span className="text-xs text-slate-500">
            {isDefaultDueDate ? '默认' : '已手动指定'} · 当前 到 {dueLabel} 为止
          </span>
        </div>
        <div className="mt-2">
          <Input
            label="这次作业做到哪天（几点由下面的「时间」决定）"
            type="date"
            min={planDate}
            value={effectiveDueDate}
            onChange={(e) => onPatchSubject({ dueDate: e.target.value || planDate })}
            hint={describeHomeworkDueDefault(planDate)}
          />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {dueQuickOptions.map((option) => {
            const selected = option.value === effectiveDueDate;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={selected}
                className={cn(
                  'h-9 rounded-full border px-3 text-xs font-medium',
                  selected
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-slate-300 bg-white text-slate-700',
                )}
                onClick={() => onPatchSubject({ dueDate: option.value })}
              >
                {option.label}
              </button>
            );
          })}
          {!isDefaultDueDate && (
            <button
              type="button"
              className="h-9 rounded-full px-3 text-xs font-medium text-brand-700 underline"
              onClick={() => onPatchSubject({ dueDate: defaultDueDate })}
            >
              恢复默认（{homeworkDueDateLabel(defaultDueDate, planDate)}）
            </button>
          )}
        </div>
        {/*
          ⚠️ 这一段只在默认策略是 `same-day`（方案丙）时才出现：
          那时的默认值仍然是当天，必须**主动建议**家长把周五/周六的作业留到周日，
          否则"周五布置、周六就超时"这件事会原样回来。
          当前策略（`next-school-day-eve`）下框里已经是周日了，所以这里永远是空的。
        */}
        {dueSuggestion && effectiveDueDate !== dueSuggestion.date && (
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 px-3 py-2">
            <span className="text-xs text-amber-800">{dueSuggestion.text}</span>
            <button
              type="button"
              className="h-8 rounded-full bg-amber-500 px-3 text-xs font-semibold text-white"
              onClick={() => onPatchSubject({ dueDate: dueSuggestion.date })}
            >
              改成{homeworkDueDateLabel(dueSuggestion.date, planDate)}
            </button>
          </div>
        )}
      </div>

      {/* 时刻：默认收起，摘要里显示已有的值（口径与保存后的展示一致：没填 + 当天 = 当天完成） */}
      <div className="mt-3 space-y-2">
        <FoldToggle
          label="时间"
          summary={describeSubjectScheduleInput({ ...subject, planDate })}
          open={schedule.open}
          onToggle={() => schedule.setOpen(!schedule.open)}
          icon={<IconClock />}
          actionLabel="设置开始 / 截止时刻"
          collapseLabel="收起开始 / 截止时刻设置"
        />
        {schedule.open && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="开始时刻（可选）"
              type="time"
              value={subject.startTime}
              onChange={(e) => onPatchSubject({ startTime: e.target.value })}
            />
            {/*
              ⚠️ 提示语的语义**不能丢**：它是 `dueTimeSource === 'dayEnd'` 那条既有口径的
              用户侧说法 —— 没填截止时刻时服务端把 `due_time` 落成 23:59，但界面必须说清
              "那一天结束前完成"。截止日就是布置日时逐字保留旧的「当天完成（不显示 23:59）」；
              截止日在别的日子时改成「到「周日 10-18」结束前完成」——
              否则家长会以为作业还是当天交（`describeHomeworkDueTimeHint()` 是唯一一份实现）。
            */}
            <Input
              label="截止时刻（可选）"
              type="time"
              value={subject.dueTime}
              disabled={subject.durationMinutes.trim() !== ''}
              onChange={(e) => onPatchSubject({ dueTime: e.target.value })}
              hint={
                subject.durationMinutes.trim() !== ''
                  ? '填了时长就不用填截止时刻'
                  : describeHomeworkDueTimeHint(effectiveDueDate, planDate)
              }
            />
            <Input
              label="时长（分钟，可选）"
              type="number"
              min={1}
              max={1440}
              inputMode="numeric"
              value={subject.durationMinutes}
              disabled={subject.dueTime.trim() !== ''}
              onChange={(e) => onPatchSubject({ durationMinutes: e.target.value })}
              hint={
                subject.dueTime.trim() !== ''
                  ? '填了截止时刻就不用填时长'
                  : `从开始时刻起算；${describeHomeworkDueTimeHint(effectiveDueDate, planDate)}`
              }
            />
          </div>
        )}
      </div>

      {/* 作业项：卡片式纵向排布（窄屏不会挤成"一个字一行"） */}
      <div className="mt-4 space-y-3">
        {subject.items.map((item, itemIndex) => (
          <ItemEditor
            key={item.key}
            item={item}
            index={itemIndex}
            forcedMoreKey={forcedMoreKey}
            onPatch={(patch) => onPatchItem(item.key, patch)}
            onRemove={() => onRemoveItem(item.key)}
          />
        ))}

        <Button size="sm" variant="secondary" onClick={onAddItem}>
          ＋ 加一项作业
        </Button>
      </div>

      {/* 作业项被全部删掉 = "这一科不要了"：保存时这一科不会进 payload。
          必须在这里说清后果，而不是等保存后被一条校验顶回来。 */}
      {subject.items.length === 0 && (
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
          这一科下面已经没有作业项了。保存后<strong>这一科会从这天的作业里移除</strong>
          （孩子已经打过卡的项只会保留记录、不会再出现在孩子端）。
          想留着就点上面的「＋ 加一项作业」。
        </p>
      )}

      {/* 这一科里有"已被移出计划、保留记录"的项：它们**不在**上面的输入框里
          （已经不在计划里了，再提交一遍等于"加回来"），但必须让家长看见，
          否则一张空的科目卡会被误读成"这一科什么都没有"。 */}
      {retainedNames.length > 0 && (
        <div className="mt-3 rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-600">
          <p>
            🗄️ 这一科另有 {retainedNames.length} 项
            <strong>已被移出这天的计划</strong>、只保留记录：
          </p>
          <ul className="mt-1 space-y-0.5">
            {retainedNames.map((name) => (
              <li key={name} className="truncate line-through">
                {name}
              </li>
            ))}
          </ul>
          <p className="mt-1">
            它们已经不在孩子端的清单里，所以<strong>不在下面的输入框里</strong>；
            想重新布置就把同名的作业项加回来（同名会接着用原来的记录）。
          </p>
        </div>
      )}

      {problems.length > 0 && (
        <ul className="mt-3 space-y-1">
          {problems.map((problem) => (
            <li
              key={problem.message}
              role="alert"
              className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700"
            >
              {problem.message}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ================================================================ 页面

/** 一周前 / 今天（历史区间的默认值，与服务端"最近 30 天"同一口径的起点）。 */
function defaultHistoryRange(): { from: string; to: string } {
  const to = todayLocal();
  return { from: addDays(to, -29), to };
}

export function ParentHomeworkPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const studentQuery = useStudent(id);
  const actor = useParentActor(studentQuery.data?.permission ?? null);
  const canEditByRole = canCreate(actor);

  const [date, setDate] = useState(() => todayLocal());
  /**
   * 三个视图：「今天的作业 / **还有效** / 超时」。
   *
   * ⚠️ 用户明确要求**分开**：「没有完成的作业任务，会一直显示，在「超时」标签中
   * 显示，直到完成或家长点击放弃」。超时是跨天累积的欠账（数据源是
   * `GET .../homework/overdue`），与"选中的这一天"是两件事 ——
   * 所以它**不随日期选择器变化**，默认这个变量为 `'today'`（录入是每天的主路径）。
   *
   * `'open'`（**还有效**，2026-10-17 新增）同样是跨天的派生视图，同样不随日期变。
   * 它补的是用户报的缝：周五布置、周日截止的作业，周六在「今天的作业」
   * （按布置日 `checkin.due_date` 筛）与「超时」（按截止时刻 `checkin.due_at < now()` 筛）
   * 里都看不见 —— 家长既看不到进度，孩子交了也无处可验收。
   */
  const [tab, setTab] = useState<'today' | 'open' | 'overdue'>('today');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [drafts, setDrafts] = useState<HomeworkDraftSubject[]>(() => [
    // 首屏（还没拿到这一天的数据）先按"今天"建一张卡：截止日期走默认策略
    emptyHomeworkSubjectDraft(todayLocal()),
  ]);
  const [problems, setProblems] = useState<HomeworkDraftProblem[]>([]);
  /**
   * 待确认的「复制上次的结构」（`null` = 没有待确认的复制）。
   *
   * 草稿里有内容时点这一下会**覆盖**这一天的内容，所以先问一句；
   * 判定由纯函数 `homeworkCopyGate()` 给出（`packages/shared`），这里只负责把
   * "有没有**还没保存**的输入"带到确认框的文案里 ——
   * 有的话必须直说"你正在录入的内容会被丢弃"，绝不能让家长以为
   * "改的是服务器上那份、跟我刚敲的字无关"。
   */
  const [pendingCopy, setPendingCopy] = useState<HomeworkCopyGate | null>(null);
  /**
   * 「放弃」的目标 + 原因。
   *
   * ⚠️ 它**必须**经过一次人话确认（见页面底部那个 Modal），而且这是家长端
   * **唯一**能把孩子永远不做的那一项从超时队列里清掉的入口 ——
   * 后端 `POST /checkins/:id/skip` 对作业项只允许家长 owner/editor 调用
   * （`canSkipHomeworkCheckin`），学生调用会 403 `HOMEWORK_SKIP_PARENT_ONLY`。
   */
  const [skipTarget, setSkipTarget] = useState<HomeworkOverdueEntry | null>(null);
  const [skipReason, setSkipReason] = useState('');
  const [skipError, setSkipError] = useState<string | null>(null);

  /**
   * 「这次保存会把某些**当前有效**的科目/作业项移出计划」的待确认快照。
   *
   * ⚠️ 它存在的唯一理由是**防数据丢失**，不是确认癖：整单覆盖的语义是
   * "提交里没有的 = 从这一天移除"，方向是危险的那一边。只要这一步能算出
   * 任何移除（`homeworkPlanRemovals()`），保存就必须停下来问一句 ——
   * 用户的原话是"**我只是加了一科，结果另一科没了**"（2026-10-13 生产事故）。
   *
   * 非 null 时页面上会弹一个把"要移除什么"逐条列清的确认框；
   * 点「确认移除并保存」才真的发请求（见 `confirmRemovals()`）。
   */
  const [pendingRemovals, setPendingRemovals] = useState<HomeworkRemovalPreview | null>(null);

  const dayQuery = useHomeworkDay(id, date);
  const mutations = useHomeworkMutations(id);
  /**
   * 「超时」队列（跨天累积的未完成作业项）。
   *
   * 排序（`dueAt` 倒序，最新截止在最上面）与"超时"判据都在**服务端**
   * （`due_at < now()` 且状态未终结），前端**不重排、不重算** ——
   * 学生端读的是同一个端点，两端因此看到同一个顺序。
   */
  const overdueQuery = useHomeworkOverdue(id);
  /**
   * 「**还有效**」队列（还没到期、但已经不是今天布置的未完成项）。
   *
   * 用户报的 bug 就是它没有归属：周五布置、周日截止的两项作业，周六在
   * 「今天的作业」（按布置日筛）与「超时」（按截止时刻筛）里**都看不见**。
   * 排序（`dueAt` 正序）与判据都在服务端，前端**不重排、不重算**。
   */
  const openQuery = useHomeworkOpen(id);
  /**
   * **验收**（通过 / 打回）—— 与"整单保存"是两套动作，原因见 `useHomeworkApproval`：
   * 验收要顺带失效积分、账本与统计（通过才计分），保存不会动分。
   *
   * ⚠️ 这套动作是**一个** hook，被两个视图同时使用：「今天的作业」里那条作业项，
   * 以及「超时」列表里那些 `submitted` 的项（2026-10-15 用户实测：
   * 昨天提交的作业只出现在超时列表里，而那里当时**没有通过按钮**）。
   * 共用同一个 hook 意味着"打的接口、权限判据、失效缓存链、文案"全都只有一份
   * —— 包括**打回原因弹窗**（`rejectDialog`，页面在根上渲染一次）。
   */
  const approval = useHomeworkApprovalActions(id);
  /** 「通过 / 打回」在飞时两个视图的按钮都要禁用（同一个 busy）。 */
  const approvalBusy = approval.busy;
  /**
   * 「放弃」走的还是打卡那一套端点（`POST /checkins/:id/skip`），
   * 所以这里复用 `useHomeworkCheckinMutations`：它的失效范围与验收那套一致
   * （作业日 + 超时队列 + 今日清单 + 积分 + 统计），正是"放弃之后界面立刻对上"所需。
   */
  const checkinMutations = useHomeworkCheckinMutations(id);
  const overdueEntries = overdueQuery.data?.entries ?? [];
  /**
   * 「还有效」的条目。去重按 `id`（= checkinId）而不是按名字：同名作业项可以是
   * **两项**真实作业（用户报的那次就是 2 项），按名字去重会吞掉孩子真要做的一项。
   * 判据与实现在 `@fht/shared` 的 `dedupeHomeworkQueueEntries()`（唯一一份）。
   */
  const openEntries = useMemo(
    () => dedupeHomeworkQueueEntries(openQuery.data?.entries ?? []),
    [openQuery.data],
  );
  const day = dayQuery.data;

  /** 服务端也判一次能不能改；两边都允许时才显示录入入口。 */
  const canEdit = canEditByRole && (day ? day.permissions.canEdit : true);

  /**
   * 录入表单的**科目候选**（"科目先定义好，下拉选，不用每次手输"）。
   *
   * 数据来源与「查过往的作业」是**同一个**接口、**同一个**区间（最近 30 天）、
   * **同一个** queryKey（见 `useHomeworkHistory`）—— 所以这里不会多打一次接口：
   * 谁先请求，另一个直接用缓存。区间用 `useMemo` 固定住 key（与历史区一致）。
   *
   * ⚠️ 它失败**不当作错误**：候选只是"少打几个字"的便利，接口挂了照样能手输科目，
   * 所以这里既不报错、也不参与保存（`data` 为空时下拉只剩今天已有的科目）。
   * 顺序是"今天这一天的科目 → 历史里最近录过的"（见 `subjectSuggestionsOf()`）。
   */
  const subjectCandidateRange = useMemo(() => defaultHistoryRange(), []);
  const subjectHistoryQuery = useHomeworkHistory(id, subjectCandidateRange);
  const subjectSuggestions = useMemo(
    () => subjectSuggestionsOf(day?.subjects, subjectHistoryQuery.data),
    [day, subjectHistoryQuery.data],
  );

  /**
   * 草稿的同步 key：**日期 + 一次"权威刷新"的令牌**。
   *
   * 为什么不是"`day` 变了就重置草稿"：切到"录入"后 react-query 仍可能因为
   * 别处的失效而重新拉这一天（例如刚提交完 / 窗口重新聚焦），
   * 那种刷新会把家长**正在输入的内容整段抹掉** —— 在手机上等于白填。
   * 所以只在两种时刻重建草稿：
   *   1. 日期变了（这本来就是"换一天重新录"）；
   *   2. 我们自己刚写完（保存 / 复制 / 清空），令牌 +1 —— 此时以服务端结果为准。
   *
   * ⚠️ 第 2 条的"服务端结果"是**那一次响应的内容**，不是"缓存里碰巧是什么"：
   * 写操作的成功回调会先把响应播种进这一天的缓存（见 `homeworkWriteOnSuccess()`），
   * 所以令牌 +1 之后的那一次渲染读到的就是写完之后的真实数据。
   * 少了那一步播种，这里重建出来的就是**写之前**的旧草稿 ——
   * 而这正是 2026-10-14 用户实测的"点了「复制上次的结构」，录入态里什么都没有"
   * （判定本身被抽成纯函数 `homeworkDraftSyncDecision()`，回归测试在
   * `apps/web/src/lib/homeworkDraftSync.test.ts`）。
   */
  const [syncToken, setSyncToken] = useState(0);
  const syncedKeyRef = useRef<string>('');
  /**
   * 上一次重建草稿时用的**日期**。
   *
   * 它回答的是"这一次同步键变化是**换了一天**，还是**我们自己刚写完**"：
   *   - 日期变了 → 视图由"这一天有没有作业"推导（有就看、没有就直接录）；
   *   - 日期没变（令牌变了）→ **保留调用方刚设的视图**：保存完看结果、清空后继续录、
   *     **在录入态点「复制上次的结构」就留在录入态**（用户要的是"在所在面看到复制过来的作业"，
   *     而不是被弹到查看态）。
   */
  const syncedDateRef = useRef<string>('');
  /**
   * 「点了「修改」，等这一天的**权威数据**回来之后重建草稿」的一次性开关。
   *
   * 为什么需要它：草稿只在"日期变了"或"我们自己刚写完"时重建（见上面的注释）是
   * **刻意的** —— 不能让后台刷新把家长正在输入的内容抹掉。但那条规则留下一个洞：
   * 如果家长打开「修改」的那一刻，`day` 还是**旧的**（孩子刚提交、刚保存过、
   * 页面在后台被失效过，刷新还没回来），表单显示的就不是这一天现在的样子。
   * 而"表单里没有的 = 从这一天移除"，所以"少显示了一科"正是最危险的那种错。
   *
   * 修法是**不动**那条保守规则，而是点「修改」时主动要一次权威数据、
   * 并在它回来时**只重建这一次**草稿（此时家长还没开始输入，不会覆盖任何手输内容）。
   * 不在任何其他时刻重建：后台刷新照旧被忽略。
   */
  const rebuildDraftOnNextDayRef = useRef(false);

  /**
   * 「这份草稿是从哪一份服务端数据构建出来的」。
   *
   * ⚠️ 它存在的唯一理由是**防数据丢失**，而且防的是**和 `homeworkDayToDraft()`
   * 不同的另一种漏**：那是"函数本身会漏"，这是"喂给它的数据已经过期"。
   *
   * 2026-10-13 生产库里那一次（审计日志还原的 08:37:26：payload 里只有数学，
   * 语文被停用保留 + 物理删除）就是后一种形状：那一刻语文的 4 项**全都是 active**，
   * 所以"表单里没有语文"用服务端当时的数据是解释不了的 ——
   * 表单拿到的不是这一天的真实数据（旧缓存 / 旧响应）。
   *
   * 记住"我这一份草稿基于哪一次响应"，页面才能在**发请求之前**回答：
   * **服务端此刻的计划，是不是比我这份草稿多东西？**
   * 多 = 表单少显示了内容 = 这一次保存会把它删掉（见 `doSave()` 的护栏）。
   */
  const draftSourceRef = useRef<HomeworkDayResult | undefined>(undefined);

  useEffect(() => {
    if (!day) return;
    const nextKey = `${day.date}#${syncToken}`;
    /**
     * 三条规则的**唯一**出处（页面里不再另写一份 `if`）：
     *   - 后台刷新（key 没变）→ 不重建，绝不抹掉家长正在输入的内容；
     *   - 换了一天 → 重建，并由"有没有作业"决定进查看还是录入；
     *   - 我们自己刚写完 → 重建（用写完之后的 `day`，见 `syncedKeyRef` 的注释），
     *     但**不改视图** —— 视图由调用方（保存 / 清空 / 复制）自己说了算。
     */
    const decision = homeworkDraftSyncDecision({
      syncedKey: syncedKeyRef.current,
      nextKey,
      syncedDate: syncedDateRef.current,
      dayDate: day.date,
    });
    if (!decision.rebuild) return;
    syncedKeyRef.current = nextKey;
    syncedDateRef.current = day.date;
    if (decision.deriveMode) {
      /**
       * 这一天**已经有**作业单 → 默认"查看"（家长大多是在检查进度）；
       * 还没有 → 直接进"录入"（少点一步，而且这是每天的主路径）。
       * 判据用 `day.exists` 而不是 `subjects.length`：整单被清空过的那天 `exists: false`，
       * 那时候家长要做的正是重新录一遍。
       */
      setMode(day.exists ? 'view' : 'edit');
    }
    draftSourceRef.current = day;
    setDrafts(dayToDraft(day, date));
    setProblems([]);
    rebuildDraftOnNextDayRef.current = false;
  }, [day, syncToken]);

  /**
   * 点「修改」：先要一次这一天的权威数据，再用它重建草稿。
   *
   * 两种结果都要能进录入态（网络失败时也不能把用户挡在门外 ——
   * 那时表单用的是手上这一份，而保存前的语义护栏会兜住"会误删"的情况）。
   */
  const enterEditMode = () => {
    setProblems([]);
    rebuildDraftOnNextDayRef.current = true;
    void dayQuery.refetch().finally(() => setMode('edit'));
  };

  /** 上面那次权威刷新回来了：用服务端最新结果重建草稿（一次性）。 */
  useEffect(() => {
    if (!day || !rebuildDraftOnNextDayRef.current) return;
    rebuildDraftOnNextDayRef.current = false;
    draftSourceRef.current = day;
    setDrafts(dayToDraft(day, date));
    setProblems([]);
  }, [day]);

  const liveSubjects = useMemo(
    () => (day?.subjects ?? []).filter((subject) => !subject.retained),
    [day],
  );
  const retainedSubjects = useMemo(
    () => (day?.subjects ?? []).filter((subject) => subject.retained),
    [day],
  );
  const liveItemCount = liveSubjects.reduce(
    (sum, subject) => sum + subject.items.filter((item) => !item.retained).length,
    0,
  );
  /**
   * 录入区要显式写出来的"保留记录"（`科目名 → 已移出计划的作业项名`）。
   *
   * ⚠️ 这一眼必须看得见：`retained` 的项不进草稿（它本来就不在这天的计划里），
   * 所以如果这一科下的项**全部**都被移出计划，卡片里就只剩一个空的科目名 ——
   * 家长会以为"这一科已经没有了"，而它其实还有孩子的打卡记录躺在那里。
   * 更糟的是，那种"看起来什么都没有"的卡片点「保存」= 把这一科整科移出计划。
   */
  const retainedItemNames = useMemo(() => retainedItemNamesOf(day), [day]);

  // ---------------------------------------------------------------- 草稿操作
  //
  // 每个改动都顺手把上一次的校验结果清掉：那些提示是"提交那一刻"的快照，
  // 家长改完之后还挂在卡片上会让人以为"改了也没用"（而它其实已经修好了）。

  const patchSubject = (key: string, patch: Partial<HomeworkDraftSubject>) => {
    setProblems([]);
    setDrafts((current) =>
      current.map((subject) => (subject.key === key ? { ...subject, ...patch } : subject)),
    );
  };

  const patchItem = (subjectKey: string, itemKey: string, patch: Partial<HomeworkDraftItem>) => {
    setProblems([]);
    setDrafts((current) =>
      current.map((subject) =>
        subject.key === subjectKey
          ? {
              ...subject,
              items: subject.items.map((item) =>
                item.key === itemKey ? { ...item, ...patch } : item,
              ),
            }
          : subject,
      ),
    );
  };

  const addSubject = () => {
    setProblems([]);
    // 新科目卡的截止日期按默认策略算（依赖"这一天是哪一天"）
    setDrafts((current) => [...current, emptyHomeworkSubjectDraft(date)]);
  };

  /**
   * 删掉一个科目。
   *
   * ⚠️ 删到**一个都不剩**时不再塞回一张空白科目卡。
   * 旧写法 `next.length > 0 ? next : [emptySubject()]` 会让"删掉最后一个科目"
   * 变成"多出一张空的科目卡"，而那张空卡过不了校验 —— 用户点「保存」只会看到
   * 「请填写科目名称」，于是"删了以后保存不了"（用户实测报的就是这个）。
   * 现在的语义：草稿为空 = 这一天不布置作业，保存时提交 `{ subjects: [] }`，
   * 服务端按整单清空处理（有打卡记录的项停用保留并给出提示）。
   */
  const removeSubject = (key: string) => {
    setProblems([]);
    setDrafts((current) => current.filter((subject) => subject.key !== key));
  };

  const moveSubject = (key: string, delta: number) => {
    setProblems([]);
    setDrafts((current) => {
      const index = current.findIndex((subject) => subject.key === key);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (!moved) return current;
      next.splice(target, 0, moved);
      return next;
    });
  };

  const addItem = (subjectKey: string) => {
    setProblems([]);
    setDrafts((current) =>
      current.map((subject) =>
        subject.key === subjectKey
          ? { ...subject, items: [...subject.items, emptyHomeworkItemDraft()] }
          : subject,
      ),
    );
  };

  /**
   * 删掉一项作业。
   *
   * ⚠️ 允许删到**一项都不剩**（旧写法在只剩 1 项时直接不删，按钮也是 disabled 的）。
   * 一个没有作业项的科目在提交时会被丢掉，语义是"这一科不要了" ——
   * 而不是弹一句"每个科目至少要填 1 项作业"把用户的删除顶回去。
   */
  const removeItem = (subjectKey: string, itemKey: string) => {
    setProblems([]);
    setDrafts((current) =>
      current.map((subject) =>
        subject.key === subjectKey
          ? { ...subject, items: subject.items.filter((item) => item.key !== itemKey) }
          : subject,
      ),
    );
  };

  /** 与"复制上次"对称的本地动作：把**当前草稿的这一科**复制一份。 */
  const duplicateSubject = (key: string) => {
    setProblems([]);
    setDrafts((current) => {
      const index = current.findIndex((subject) => subject.key === key);
      const source = current[index];
      if (!source) return current;
      const copy: HomeworkDraftSubject = {
        ...source,
        key: newHomeworkDraftKey('subject'),
        // 名称留空：同名会被服务端当成同一科覆盖掉（科目重名会被拒），必须让家长自己起名
        name: '',
        items: source.items.map((item) => ({ ...item, key: newHomeworkDraftKey('item') })),
      };
      const next = [...current];
      next.splice(index + 1, 0, copy);
      return next;
    });
    toast.showToast('已复制一份，记得改科目名（同名会被当成同一科）', 'info');
  };

  // ---------------------------------------------------------------- 保存 / 复制

  /**
   * 保存（**带语义护栏**）。
   *
   * 顺序刻意是"先本地校验 → 再算这次会移除什么 → 最后才发请求"：
   *
   *  1. 本地校验（`validateHomeworkDraft`）：科目名、时刻、分值那些"一眼可见的错"，
   *     拦下来不让用户白等一次网络往返；
   *  2. **语义护栏**（`homeworkPlanRemovals`）：拿"这一次要提交的入参"与
   *     "这一天当前计划里的科目与作业项"对一遍。只要会移除**当前有效**的东西，
   *     就**不直接保存**，而是弹出确认框把"会被移除的东西"逐条说清。
   *
   * ⚠️ 为什么必须有第 2 步（而不是只靠草稿完整）：整单覆盖的语义是
   * "提交里没有的 = 从这一天移除"，方向是**危险的那一边** ——
   * 草稿构建将来再漏掉任何一种分支，用户"只是加一科"就会静默删掉另一科
   * （2026-10-13 生产事故就是这么发生的）。草稿完整（`homeworkDayToDraft()`）
   * 是"正常情况下的确定性保证"，这道护栏是"漏了也删不掉"的兜底。
   *
   * 判据刻意与页面的「删除」按钮语义**精确对齐**：
   *  - 计划里的科目不在入参里 → 会被移除；
   *  - 计划里某科下的作业项不在入参的同一科里 → 会被移除；
   *  - 匹配按**名称**（与服务端 `applyHomeworkSave()` 一字不差），
   *    所以"改名"也会被列出来 —— 那会真的丢掉原记录，必须让用户看见。
   *
   * `retained`（已移出计划）的科目/作业项不在计划里，永远不会被列进来。
   */
  const doSave = async () => {
    const found = validateHomeworkDraft(drafts, date);
    setProblems(found);
    if (found.length > 0) {
      toast.showToast(found[0]?.message ?? '请检查录入内容', 'error');
      return;
    }
    /**
     * 草稿里一个科目都不剩 = "这一天不布置作业"（删光了，或本来就是空的）。
     * 提交 `{ subjects: [] }` 是服务端认可的语义：整单清空，
     * 有打卡记录的作业项只停用保留（响应里的 `warnings` 会说出来）。
     * 但这一天本来就还没有作业单时不必发请求 —— 那只会让服务端建一个空壳再删掉。
     */
    const input = homeworkDraftToInput(drafts);
    if (input.subjects.length === 0 && !day?.exists) {
      toast.showToast('这一天还没有作业，没什么可保存的', 'info');
      return;
    }
    /**
     * ⚠️ **保存前再看一眼服务端**（`homeworkFormDrift()`）。
     *
     * 这一步防的是与"草稿构建会漏"**不同的另一种漏**：草稿构建本身是对的
     * （`homeworkDayToDraft()` 只看 `retained`，一个不落地带出所有科目与作业项），
     * 但**喂给它的数据已经过期** —— 于是表单里少了一科，
     * 而"表单里没有的 = 从这一天移除"会把它删掉，且**不会报任何错**。
     *
     * 2026-10-13 生产库里那一次（审计日志 08:37:26：payload 里只有数学，
     * 语文 2 项被停用保留、2 项被物理删除）就是这种形状：
     * 那一刻语文的 4 项**全都是 active**（直到那次保存才被停用），
     * 所以"表单里没有语文"用服务端当时的数据解释不了 —— 表单拿到的是旧数据。
     *
     * 判据（只有一边，见 `homeworkFormDrift()` 的注释）：
     * **服务端此刻的计划里有、而我这份草稿的底稿里没有的内容** = 表单少显示了东西。
     * 命中就**不发请求**，改用刚取到的最新数据重建表单，再让家长确认一次。
     *
     * 为什么放在这道护栏而不是只靠"点修改时先 refetch"：
     * 那一次 refetch 发生在**打开表单之前**，而家长往往要在表单里填很久。
     * 只要那一次响应是旧的（缓存命中、页面在后台被失效过、请求失败后
     * 用兜底数据进了录入态），表单就会带着旧内容一直走到保存。
     * 保存是**唯一真正会造成后果**的时刻，所以最后一道检查必须在这里。
     */
    let fresh = day;
    try {
      const refetched = await dayQuery.refetch();
      if (refetched.data) fresh = refetched.data;
    } catch {
      // 取不到最新数据不是"拒绝保存"的理由：下面的语义护栏照常工作
    }
    /**
     * ⚠️ 只有"**确实重取到了新的一版**、而且我手里有底稿"时才判漂移。
     *
     * 这两个前提都必须显式写出来，否则会变成"**误拦**"：
     *  - `fresh !== day`：没取到新数据时（`refetch` 失败/被取消）不能拿同一份
     *    数据去和自己比 —— 那只会让"点保存没反应"这种更难查的故障出现；
     *  - `draftSourceRef.current` 存在：它的赋值**总是紧挨着** `setDrafts(dayToDraft(...))`
     *    （见上面两处 effect 与下面的重建分支），所以正常路径下它一定在；
     *    万一不在，说明草稿不是从任何一次服务端响应构建的（初始态），
     *    那时"底稿为空"会让每一个科目都算成漂移，等于把整个人工路径堵死。
     */
    if (fresh && fresh !== day && draftSourceRef.current) {
      const drift = homeworkFormDrift(homeworkFormSnapshot(draftSourceRef.current), fresh);
      if (hasHomeworkFormDrift(drift)) {
        // 表单少显示了内容 → 用最新数据重建，绝不用这份旧草稿去覆盖服务端
        draftSourceRef.current = fresh;
        setDrafts(dayToDraft(fresh, date));
        setProblems([]);
        setSyncToken((token) => token + 1);
        toast.showToast(describeHomeworkFormDrift(drift), 'error');
        return;
      }
    }
    /**
     * ⚠️ 这道护栏只在"这一天真的有东西可移除"时才有话说。
     * 注意它对照的是 `fresh`（服务端的当天视图），不是草稿 ——
     * 所以"草稿里压根没有它"这种漏，正好是它要抓的东西。
     */
    const removals = homeworkPlanRemovals(fresh, input);
    if (hasHomeworkRemovals(removals)) {
      setPendingRemovalAction('save');
      setPendingRemovals(removals);
      return;
    }
    await submitSave(input);
  };

  /** 真正发请求的那一步（`doSave` 与确认框共用，避免两条分支各写一遍副作用）。 */
  const submitSave = async (input: ReturnType<typeof homeworkDraftToInput>) => {
    try {
      const result = await mutations.save.mutateAsync({ date, input });
      // 服务端的 retained / warnings 原样用人话展示 —— 这是"我明明删了它怎么还在"的唯一解释
      if (result.warnings.length > 0) {
        toast.showToast(result.warnings.join('；'), 'info');
      } else if (input.subjects.length === 0) {
        toast.showToast('已清空这一天的作业', 'info');
      } else {
        toast.showToast(result.created ? '已保存这一天的作业' : '已更新这一天的作业', 'success');
      }
      setMode('view');
      /**
       * 刚写完：令牌 +1 → 用**这一次的响应**重建草稿，并进入"查看"。
       *
       * ⚠️ 那一次渲染读到的 `day` 就是写完之后的真实数据 —— 写操作的成功回调
       * 已经先把响应播种进这一天的缓存（`homeworkWriteOnSuccess()`）。
       * 少了播种，这里重建出来的就是**写之前**的旧草稿，而且同步键已经被消费掉
       * → 新数据回来反而被当成"后台刷新"忽略（用户实测的"复制后表单不刷新"
       * 就是这条路径的形状，见 `syncToken` 与 `homeworkDraftSyncDecision()` 的注释）。
       */
      setSyncToken((token) => token + 1);
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  /**
   * ⚠️ 那个确认框有两个入口，语义不同，必须分开：
   *   - `'save'`：这次保存会移除某些东西（**用户多半没想删**）→ 确认后提交草稿；
   *   - `'clear'`：用户点了「清空这一天的作业」（**移除全部就是他的本意**）→
   *     确认后提交 `{ subjects: [] }`。
   * 两种都必须是**显式**的，因为一旦发出去，"这一天的计划没了"就是既成事实
   * （孩子的记录会保留，但它不会再出现在孩子端）。
   */
  const [pendingRemovalAction, setPendingRemovalAction] = useState<'save' | 'clear'>('save');

  /** 用户在确认框里明确点了"确认移除并保存"。 */
  const confirmRemovals = async () => {
    if (pendingRemovalAction === 'clear') {
      setPendingRemovals(null);
      await clearDay();
      return;
    }
    const input = homeworkDraftToInput(drafts);
    setPendingRemovals(null);
    await submitSave(input);
  };

  const doCopyLast = async () => {
    try {
      const result = await mutations.copy.mutateAsync({ date });
      toast.showToast(
        `已复制 ${result.copiedFrom.date} 的作业结构（${result.copiedFrom.subjectCount} 个科目 · ` +
          `${result.copiedFrom.itemCount} 项作业）。完成状态不会复制，今天要从头做。`,
        'success',
      );
      if (result.warnings.length > 0) toast.showToast(result.warnings.join('；'), 'info');
      /**
       * 令牌 +1 = "刚写完，请用**服务端这一次的响应**重建草稿"。
       *
       * ⚠️ 它能不能重建出复制过来的内容，取决于那一刻缓存里的 `day` 是不是新的：
       * 写操作的成功回调已经把这个响应播种进"这一天"的缓存
       * （`homeworkWriteOnSuccess()`），所以紧接着的这一次渲染读到的一定是
       * 复制之后的真实数据 —— 旧写法缺的就是这一步，于是"录入态的草稿"没被重建
       * （用户看到的就是"表单里什么都没有"）。
       *
       * ⚠️ 这里**不** `setMode('view')`：视图留在家长点复制的那一屏，
       * 由 `homeworkDraftSyncDecision()` 的 `deriveMode: false` 保证不被旧数据的
       * `exists` 顶回去（见页面里 `syncedDateRef` 的注释）。
       */
      setSyncToken((token) => token + 1);
    } catch (err) {
      /**
       * `HOMEWORK_NO_SOURCE`（404）是最常见的一种：目标日期之前还没有任何一次录入。
       * 它不是故障，而是一条操作指引 —— 服务端已经给了可读文案，这里原样展示。
       */
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  /**
   * 「清空这一天的作业」。
   *
   * ⚠️ 它走的是同一个整单覆盖接口（`subjects: []`），所以它**是**一次"移除全部"的
   * 声明 —— 只不过这次移除全部是用户的本意。它同样要经过那个把"会被移除什么"
   * 逐条列清的确认框（`pendingRemovals`），因为这句话的后果比按钮文字看起来大：
   * 孩子已经打过卡的项只会被移出计划、记录保留，但**这一天的计划整个没了**。
   */
  const requestClearDay = () => {
    setPendingRemovalAction('clear');
    setPendingRemovals(homeworkPlanRemovals(day, { subjects: [] }));
  };

  const clearDay = async () => {
    try {
      const result = await mutations.save.mutateAsync({ date, input: { subjects: [] } });
      if (result.warnings.length > 0) toast.showToast(result.warnings.join('；'), 'info');
      else toast.showToast('已清空这一天的作业', 'info');
      setMode('edit');
      // 刚写完：令牌 +1 → 用这一次的响应重建草稿（它已经被播种进这一天的缓存），
      // 清空之后继续停在录入态（见 `syncToken` 的注释）。
      setSyncToken((token) => token + 1);
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  /**
   * 「复制上次的结构」。
   *
   * ⚠️ 它是**写入**而不是"把内容填进表单"：服务端直接落库并把当天的作业单返回。
   * 因此当草稿里已经有家长手输的内容时要先问一句 —— 那一次点击会把它整个覆盖掉，
   * 而按钮就在"录入"区上方，手机上很容易误触。草稿本来就是空的就直接执行，
   * 少一次无意义的确认。
   *
   * ⚠️ 这里**刻意不切视图**（旧写法是 `setMode('view')`）：
   * 家长在哪一屏点的复制就留在哪一屏 —— 在录入态点它是"给这一天找个起点，
   * 接下来要改"，把人踢到查看态等于让他再点一次「修改」。
   * 草稿则由写完之后的 `day` 重建（同步令牌 +1，见上面 `syncToken` 的注释：
   * 那一次响应的内容已经被播种进缓存），所以**录入表单里立刻就是复制过来的科目与作业项**。
   */
  const requestCopy = () => {
    const gate = homeworkCopyGate(drafts, draftSourceRef.current);
    if (gate.needsConfirm) {
      setPendingCopy(gate);
      return;
    }
    void doCopyLast();
  };

  const busy = mutations.save.isPending || mutations.copy.isPending;
  const notReady = dayQuery.isError;
  const isToday = date === todayLocal();
  /**
   * 刚切了日期、新一天的数据还没回来时，`day` 仍是上一天的那一份。
   * 此时必须显示骨架屏 —— 否则会把昨天的科目当成今天的渲染出来，
   * 家长一眼就会以为"我昨天的作业怎么跑到今天了"。
   */
  const loading = !day || day.date !== date;

  // ---------------------------------------------------------------- 验收
  //
  // 📌 2026-10-15：这一段（`doApprove` / `openReject` / `confirmReject` / 打回弹窗）
  // 原来写在这个文件里，于是它只服务于「今天的作业」—— 而「超时」是跨天的派生视图
  // （昨天提交的作业项**只出现在那里**），家长因此"想点通过却没有按钮"（用户实测原话）。
  //
  // 现在**整套动作**（打的接口、权限判据、失效缓存链、Toast 文案、打回原因弹窗）
  // 都在 `lib/homeworkApprovalActions.tsx` 的 `useHomeworkApprovalActions()` 里，
  // 由「今天的作业」与「超时」两个视图共用（`approval` 已在上面声明）。
  // ⛔ 不要在这里再写一份 —— 那正是这个 bug 的形状。

  /** 把一条作业项转成验收动作的入参（唯一一处"作业项 → checkinId"的翻译）。 */
  const approveItem = (item: HomeworkItemView) =>
    approval.onApprove(itemApprovalTarget(item, date));
  const rejectItem = (item: HomeworkItemView) =>
    approval.onReject(itemApprovalTarget(item, date));

  /** 「超时」行转成验收动作的入参（那边 `id` **就是** checkinId，见 `HomeworkOverdueEntry`）。 */
  const approveOverdue = (entry: HomeworkOverdueEntry) =>
    approval.onApprove({
      checkinId: entry.id,
      name: entry.habitName,
      pointsPerUnit: entry.pointsPerUnit,
      date: entry.date,
    });
  const rejectOverdue = (entry: HomeworkOverdueEntry) =>
    approval.onReject({
      checkinId: entry.id,
      name: entry.habitName,
      pointsPerUnit: entry.pointsPerUnit,
      // 跨天的项必须写清属于哪一天，否则弹窗里就是一个"没有出处"的名字
      date: entry.date,
    });

  // ---------------------------------------------------------------- 放弃（超时队列）

  const openSkip = (entry: HomeworkOverdueEntry) => {
    setSkipTarget(entry);
    setSkipReason('');
    setSkipError(null);
  };

  /**
   * 「放弃」= `POST /checkins/:id/skip`（**仅家长 owner/editor**）。
   *
   * 服务端语义（决定了这段文案与提示怎么写，见契约的「放弃」一节）：
   *   - 这一项终结成 `skipped`：**立刻退出超时队列**、不再阻塞科目完成判定，
   *     孩子端显示"家长已放弃这一项，不用再做了"；
   *   - 它**不算完成**（科目完成要求至少 1 项 `done`，全部放弃 ≠ 完成）；
   *   - **打卡记录与积分依据不删**（`skip_reason` 会存下来，孩子看得到）；
   *   - `reason` 上限 200 字（`skipCheckinSchema`），留空是允许的。
   *
   * 失败时**不吞**后端文案：最典型的是家长只有 `viewer` 权限，
   * 后端返回 403 + `HOMEWORK_SKIP_PARENT_ONLY`（"作业的「放弃」只能由家长来点…"），
   * 那句话本身就把原因说清了，直接弹出来即可。
   */
  const confirmSkip = async () => {
    if (!skipTarget) return;
    const checkinId = skipTarget.id;
    if (!checkinId) {
      toast.showToast('这一项没有可放弃的打卡记录，刷新一下再试', 'error');
      setSkipTarget(null);
      return;
    }
    setSkipError(null);
    try {
      await checkinMutations.skip.mutateAsync({
        checkinId,
        reason: skipReason.trim(),
        // 一次用户意图一个幂等键：弱网重试不会产生第二条放弃记录
        idempotencyKey: newIdempotencyKey(`skip:${checkinId}`),
      });
      toast.showToast(describeSkipResult(skipTarget.habitName, skipReason), 'success');
      setSkipTarget(null);
      setSkipReason('');
    } catch (err) {
      const message = toErrorMessage(err);
      setSkipError(message);
      toast.showToast(message, 'error');
      setSkipTarget(null);
    }
  };

  // ---------------------------------------------------------------- 撤销放弃（家长）

  /**
   * 「恢复成未完成」= `POST /checkins/:id/restore`（`skipped → pending`）。
   *
   * 为什么家长也需要它：家长在「超时」里点了「放弃」，过一会儿发现这一项其实该做
   * （或者孩子说"我还要做"）—— 之前没有任何回程，只能眼睁睁看它一直是"已放弃"。
   * 权限上家长**不受日期限制**（`decideRestore()`：孩子只限当天，家长任意）。
   *
   * 不做二次确认：恢复是**可逆的安全动作**（退回未完成还能再放弃），
   * 多一次点击只会让"救回来"变慢。失败时不吞后端文案（例如 viewer 家长会收到 403）。
   */
  const restoreItem = async (item: HomeworkItemView) => {
    const checkinId = item.checkin?.checkinId;
    if (!checkinId) {
      toast.showToast('这一项没有可恢复的打卡记录，刷新一下再试', 'error');
      return;
    }
    try {
      const result = await checkinMutations.restore.mutateAsync({
        checkinId,
        // 一次用户意图一个幂等键：弱网重试不会写第二条恢复事件
        idempotencyKey: newIdempotencyKey(`restore:${checkinId}`),
      });
      toast.showToast(
        result.reversedDelta !== 0
          ? `已把「${item.name}」恢复成未完成，并退回了 ${-result.reversedDelta} 分`
          : `已把「${item.name}」恢复成未完成，孩子端可以接着做`,
        'success',
      );
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  return (
    <AppShell
      kind="parent"
      title={studentQuery.data ? `${studentQuery.data.nickname} · 学校作业` : '学校作业'}
      /*
        ⚠️ 这里**刻意没有**「返回」按钮（2026-10 用户真机实测）。

        改之前它同时有：
          - `backTo={`/students/${id}`}` → 左上角的「←」；
          - `headerAction={<Link to={`/students/${id}`}><Button>返回</Button></Link>}`
            → 右上角**又一个去同一个地方**的「返回」。
        用户原话："返回键和箭头的功能好像有点重复，要么就保留箭头，要么就保留返回键吧"。

        现在只保留左上角的「←」（手机上的标准位置），它的目标由
        `lib/backNav.ts` 按路由推导为 `/students/:id`（孩子详情），
        也就是本页层级上的上一级 —— 与"从哪一页点进来的"无关。
      */
    >
      <div className="space-y-5">
        {/*
          ---------- 三个视图：今天的作业 / 还有效 / 超时 ----------

          与**学生端同一套三个标签**（`StudentHomeworkPage`）：用户明确要求
          「超时」与「今天的作业」分开，而"超时"是**跨天累积的欠账**，
          不是"某一天的作业"（所以它不受下面日期选择器的影响）。
          「**还有效**」（2026-10-17 新增）同样跨天、同样不受日期影响，它补的是
          周五布置、周日截止的项在周六「今天的作业」与「超时」里都看不见的那条缝。

          ⚠️ 这个切换器放在日期选择器**之外**，而且每一条队列区块自己有一套
          加载/错误/空状态：某一天的作业单加载失败不该把队列也一起藏起来 ——
          那正是家长最需要看到的那一屏（孩子永远不做的那一项要在这里清掉）。
        */}
        <div role="tablist" aria-label="作业视图" className="flex gap-2">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'today'}
            onClick={() => setTab('today')}
            className={cn(
              'flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
              tab === 'today'
                ? 'bg-brand-600 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200',
            )}
          >
            📚 今天的作业
            {day?.exists && day.summary.itemCount > 0
              ? ` ${day.summary.doneCount}/${day.summary.itemCount}`
              : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'open'}
            onClick={() => setTab('open')}
            className={cn(
              'flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
              tab === 'open'
                ? 'bg-sky-600 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200',
            )}
          >
            📌 还有效
            {openEntries.length > 0 ? ` ${openEntries.length}` : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'overdue'}
            onClick={() => setTab('overdue')}
            className={cn(
              'flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
              tab === 'overdue'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200',
            )}
          >
            ⏰ 超时
            {overdueEntries.length > 0 ? ` ${overdueEntries.length}` : ''}
          </button>
        </div>

        {tab === 'open' ? (
          <OverdueSection
            entries={openEntries}
            query={openQuery}
            canSkip={canEdit}
            busy={approvalBusy || checkinMutations.skip.isPending}
            kid={false}
            queue="open"
            onSkip={openSkip}
            /*
              ⚠️ 验收回调**必须传**，理由与「超时」那一支逐字相同：孩子今天提交、
              而截止日还在后面时，那一项**只**出现在「还有效」里。不传 = 家长在
              唯一看得见它的地方没有「通过」按钮（2026-10-15 那个 bug 的形状）。
              两个视图共用**同一个** `approval`（`useHomeworkApprovalActions`），
              所以打的接口、权限、失效缓存链、打回弹窗、Toast 文案全都只有一份。
            */
            onApprove={approveOverdue}
            onReject={rejectOverdue}
          />
        ) : tab === 'overdue' ? (
          <OverdueSection
            entries={overdueEntries}
            query={overdueQuery}
            canSkip={canEdit}
            busy={approvalBusy || checkinMutations.skip.isPending}
            kid={false}
            onSkip={openSkip}
            /*
              ⚠️ 这两个回调**必须传**：昨天提交、今天才变成超时的作业项只会出现在
              这个列表里，而验收动作（通过/打回）是家长在这一屏唯一能做的事。
              不传 = 界面写着"点通过后会消失"却没有按钮（2026-10-15 用户实测的 bug）。
              它们与「今天的作业」用的是**同一个** `approval`（`useHomeworkApprovalActions`），
              所以打的接口、权限、失效缓存链、打回弹窗、Toast 文案全都只有一份。
            */
            onApprove={approveOverdue}
            onReject={rejectOverdue}
          />
        ) : (
          /* ↓ 「今天的作业」这一支 = 原来的整页内容（日期选择器 + 这天的作业 + 历史）。
               刻意**没有**把它整体再缩进两格：那会把数百行已有内容变成纯空白 diff，
               在这里加一行说明比制造一次看不懂的 diff 更值。 */
          <>
        {/* ---------- 选日期（默认今天，可切到任意一天） ---------- */}
        <Card>
          {/*
            ⚠️ 2026-10-14 **第二次**改这一块：用户澄清了「对齐」的确切含义 ——

              「日期选择器在它自己的区域内和"看哪一天的作业"一起靠左排列；
                下面的"复制上次的结构"/"退出录入"在它自己的区域内和
                "2026-10-14 的作业"一起靠左排列；
                **同时日期选择器的左箭头和"复制上次的结构"的左侧要垂直对齐**。」

            验收标准因此是**两个左边缘的 x 坐标相同（≤1px）**，不是"看起来靠左"。

            ⚠️ 上一版（`flex-wrap` + 文字 `basis-full sm:basis-auto`）在 <640px **碰巧**
            是对的（文字占满第一行 → 控件组换行 → 从最左边开始），**≥640px 就崩了**：
            实测 640 / 768 / 1024 / 1280px 下 `←` 的 left = **155**、
            `复制上次的结构` 的 left = **45** —— 差 **110px**；而且日期框被 `flex-1`
            拉到 **336 / 464 / 720 / 976px**（1280px 下几乎顶到右边缘，正是用户截图里
            那个"拉得过宽"的日期输入框）。

            修法（两条一起，缺一不可）：
            1. **纵向堆叠**：`看哪一天的作业` 永远是**上面的一行**（`flex-col` +
               `items-start`，**不带** `sm:` 变体）→ 控件组从卡片的左内边距开始，
               与下面那张卡里 `basis-full` 的按钮组从**同一个 x** 开始。这是两个左边缘
               对齐的**结构性**保证（不靠"看起来刚好"），320~1280px 全都成立。
            2. **日期框不再 `flex-1`**：`w-44`（176px）+ `min-w-0`。
               `min-w-0` 是必须的 —— flex item 的自动最小尺寸是内容的 min-content，
               不给它就没有任何收缩余地（上一版踩过这个坑）。320px 时卡片内容区只有 254px，
               44+8+176+8+44 = 280 > 254，于是日期框**收缩到 150px**，箭头不变形、整行不溢出；
               390px 起就稳定在 176px，不会再被拉宽。

            ⚠️ 既有修复照守、不许弄回去：控件组仍是 `items-end`（底对齐）、两个箭头仍是
            `size="icon"`（44×44）与 input 的 `h-11` 等高，input 上**不要**加 `hint`/`error`
            （它们会在 input 下面多出一行，把底边推下去、三个控件重新错位）。
            ⚠️ 别再给日期框写 `flex-1`：那正是"拉得过宽"和"把箭头推到右边"的来源。
          */}
          <div className="flex flex-col items-start gap-2">
            {/* 标签**在上面**（不是并排）：控件组因此从卡片左内边距开始，
                与下方「复制上次的结构」的左边缘在竖直方向对齐。 */}
            <p className="text-sm font-medium text-slate-700">看哪一天的作业</p>
            {/*
              ⚠️ 三个控件的**垂直对齐**（2026-10-14 用户实测："这两个箭头没有和
              日期选择器水平对齐"）：容器是 `items-end`（底对齐），FieldShell 下面没有
              hint/error 时它的底边**就是** input 的底边；两个箭头是 `size="icon"` = 44px，
              与 input 的 `h-11` 同一个值 —— 三者底边、高度全都一致，不依赖任何
              "覆盖 size"的类名顺序技巧。
              📌 同一个 2026-10-14 又去掉了那个「日期」标签（用户要求）：`label=""` +
              `aria-label="选择日期"`（见 `components/ui/Input.tsx` 文件头），
              去掉之后 FieldShell 只剩 input 本身（44px），与两个箭头**完全等高**。
              ⚠️ 别改回 `items-center`；也别给 `Input` 加 `hint`/`error` 而不检查这里。
            */}
            <div className="flex w-full items-end gap-2">
              <IconAction
                label="前一天"
                icon={<IconChevronLeft />}
                onClick={() => setDate((current) => addDays(current, -1))}
              />
              {/*
                ⚠️ `w-44 min-w-0`：**固定宽度的日期框**（不是 `flex-1`）。
                - `w-44` = 176px：装得下 `2026-10-14`，又不会在宽屏上被拉成近千像素；
                - `min-w-0`：flex item 的自动最小尺寸 = 内容的 min-content，不给它
                  就一点都缩不动（上一版量出来的坑）；给了它在 320px 上才能收到 150px。
                ⚠️ 这一行**刻意不写 `flex-wrap`**：实测 320px 下卡片内容区只有 254px，
                三个控件要 280px —— 一旦允许换行，`→` 会自己掉到第二行
                （实测 `→` 的 top 从 117 变成 169），而不是让日期框收缩。
                不换行 + `min-w-0` 的结果才是"先收缩日期框、箭头不变形、整行不溢出"。
              */}
              <div className="w-44 min-w-0">
                <Input
                  /* 空 label = 不渲染那两个字；可访问名字改由 aria-label 提供。 */
                  label=""
                  aria-label="选择日期"
                  type="date"
                  value={date}
                  max={addDays(todayLocal(), 365)}
                  onChange={(e) => {
                    if (e.target.value) setDate(e.target.value);
                  }}
                />
              </div>
              <IconAction
                label="后一天"
                icon={<IconChevronRight />}
                onClick={() => setDate((current) => addDays(current, 1))}
              />
            </div>
            {/* 「回到今天」**不在上控件组里**：它在最窄的 320px 上会把日期框挤到 66px。
                放在下一行、仍然贴左（`items-start`），`←` 的左边缘因此不受它影响。 */}
            {!isToday && (
              <Button size="sm" variant="ghost" onClick={() => setDate(todayLocal())}>
                回到今天
              </Button>
            )}
          </div>
        </Card>

        {/* ⚠️ 外层这个 `relative` 是给下面那根 `sticky` 保存栏用的：
            它必须落在**已经加载完**的那一支里，否则骨架屏 / 错误态时也会出现一根
            "保存这一天的作业"——而那时候页面上根本没有可保存的内容。
            用 `relative` 而不是把保存栏提到 `AppShell` 外：`sticky` 需要知道它属于
            哪一块内容，放在页面根上会在手机底部导航栏下面互相顶。 */}
        <div className="relative space-y-5">
          {/* ⚠️ 顺序必须先是错误、再是骨架：接口失败时 `day` 仍是 undefined，
              若把 `loading` 放前面，页面会永远停在骨架屏上（看不到错、也重试不了）。 */}
          {notReady ? (
            <ErrorState
              message={toErrorMessage(dayQuery.error)}
              onRetry={() => void dayQuery.refetch()}
            />
          ) : loading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <>
              {/* ---------- 这天的概况 ---------- */}
              <Card
                className={cn(day?.summary.allCompleted && 'border-emerald-200 bg-emerald-50/50')}
              >
                <CardHeader
                  title={day?.sheet?.name ?? `${date} 的作业`}
                  description={
                    day?.exists
                      ? `共 ${day.summary.subjectCount} 个科目 · ${day.summary.itemCount} 项作业`
                      : '这一天还没有录入作业'
                  }
                  /*
                    ⚠️ 2026-10-14 用户实测：「我红框圈出的两个部分，请让它们左对齐，靠左摆。」
                    这一组按钮原先固定在卡片右上角（`CardHeader` 的 `justify-between`）。
                    现在 `actionAlign="start"` + 容器自己 `basis-full justify-start`：
                    它**独占一行、落在标题下面、从最左边开始**（而不是把标题挤窄 ——
                    实测 320px 下 `justify-between` 会把标题「2026-10-14 的作业」压到 28px 宽，
                    只剩一个省略号）。
                    ⚠️ `CardHeader` 那个新 prop 默认仍是 `'end'`，别的页面一个字都没变。
                  */
                  actionAlign="start"
                  action={
                    canEdit ? (
                      <div className="flex shrink-0 flex-wrap basis-full justify-start gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={mutations.copy.isPending}
                          disabled={busy}
                          onClick={requestCopy}
                        >
                          复制上次的结构
                        </Button>
                        <Button
                          size="sm"
                          variant={mode === 'edit' ? 'secondary' : 'primary'}
                          disabled={busy}
                          /* 进录入态一律走 enterEditMode()：它会先要一次权威数据，
                             免得表单显示的是"这一天过去的样子"（少显示一科 = 保存时删掉它） */
                          onClick={() => (mode === 'edit' ? setMode('view') : enterEditMode())}
                        >
                          {mode === 'edit' ? '退出录入' : day?.exists ? '修改' : '开始录入'}
                        </Button>
                      </div>
                    ) : undefined
                  }
                />

              {day?.exists && day.summary.itemCount > 0 && (
                <>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    <span className="font-semibold text-slate-900">
                      已完成 {day.summary.doneCount}/{day.summary.itemCount} 项
                    </span>
                    <span className="text-slate-500">
                      科目完成 {day.summary.completedSubjectCount}/{day.summary.subjectCount}
                    </span>
                    {day.summary.submittedCount > 0 && (
                      <Badge tone="warn">待验收 {day.summary.submittedCount}</Badge>
                    )}
                    {day.summary.abandonedCount > 0 && (
                      <Badge tone="neutral">已放弃 {day.summary.abandonedCount}</Badge>
                    )}
                    {day.summary.allCompleted && <Badge tone="success">✅ 全部完成</Badge>}
                  </div>
                  <div
                    className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={day.summary.itemCount}
                    aria-valuenow={day.summary.doneCount + day.summary.abandonedCount}
                    aria-label="作业完成进度"
                  >
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-[width]"
                      style={{
                        width: `${Math.round(
                          ((day.summary.doneCount + day.summary.abandonedCount) /
                            Math.max(1, day.summary.itemCount)) *
                            100,
                        )}%`,
                      }}
                    />
                  </div>
                </>
              )}

              {/*
                📌 这里原本有一段**冗长说明**（"这一天还没有作业。可以点「复制上次的结构」
                把上一次的科目与作业项搬过来（不会复制完成状态），也可以直接录入。"），
                2026-10-14 按用户要求从界面上**去掉**。它没有被丢弃 —— 同一件事仍然由
                这一张卡的 `CardHeader`（description = 「这一天还没有录入作业」）
                与右上角那两个按钮（「复制上次的结构」/「开始录入」）当场表达出来，
                这也是更短的那种说法。去向总账见文件里的 `HELP_MOVED_TEXTS` 注释。
              */}
              </Card>

            {/* ---------- 录入 ---------- */}
            {mode === 'edit' && canEdit ? (
              <section aria-labelledby="homework-edit-heading" className="space-y-4">
                <h2
                  id="homework-edit-heading"
                  className="text-sm font-semibold uppercase tracking-wide text-slate-500"
                >
                  录入 {date} 的作业
                </h2>

                {/*
                  📅 这一天的**默认截止日期**（整单口径的一句话）。
                  为什么还要在科目卡之外再说一遍：家长看到的第一眼问题就是
                  "这次作业到底算到哪天" —— 每个科目卡里都有这一行（可以单独改），
                  但"默认是哪天、为什么"必须在这一层就说清楚，不用先展开任何东西。
                  文案的唯一来源是 `describeHomeworkDueDefault()`（纯函数，有测试）。
                */}
                <p className="rounded-xl bg-brand-50 px-3 py-2 text-xs text-brand-900">
                  📅 {describeHomeworkDueDefault(date)}
                  {defaultHomeworkDueDate(date) !== date && (
                    <> —— 每个科目都可以单独改（科目卡里的「📅 截止日期」）。</>
                  )}
                  {defaultHomeworkDueDate(date) === date && <>；想留到周末就改「📅 截止日期」。</>}
                </p>

                {/* 草稿里一个科目都不剩：说清"保存会发生什么"，而不是塞一张空的科目卡。
                    空卡过不了校验，正是"删了以后保存不了"的来源。 */}
                {drafts.length === 0 && (
                  <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    这一天已经没有科目了。点「保存这一天的作业」= <strong>这一天不布置作业</strong>
                    ：作业单会被清空；孩子<strong>已经打过卡</strong>的作业项只会从计划里移出、
                    记录与积分依据保留（保存后会明确提示是哪些）。
                    想继续录就点下面的「＋ 加一个科目」。
                  </p>
                )}

                {drafts.map((subject, subjectIndex) => {
                  const subjectProblems = problems.filter(
                    (problem) => problem.subjectIndex === subjectIndex,
                  );
                  /* 这一科里"已被移出计划、因此不在下面这张卡上"的项（名字列表）。
                     ⚠️ 它**必须**显示出来：否则一张只剩科目名的卡片看起来像"这一科没了"，
                     而它下面其实还躺着孩子的打卡记录。 */
                  const retainedNames = retainedItemNames.get(subject.name) ?? [];
                  return (
                    <SubjectEditor
                      key={subject.key}
                      subject={subject}
                      index={subjectIndex}
                      subjectCount={drafts.length}
                      planDate={date}
                      suggestions={subjectSuggestions}
                      problems={subjectProblems}
                      /*
                        ⚠️ 校验错误指向的输入框可能在**折叠区里**（「填了目标值就必须填单位」
                        指的就是「单位」那一格）。把这类错误变成"强制展开"的开关：
                        否则家长只看到一句错误、却找不到该改的地方。
                        键用**错误文案**而不是数组引用 —— 每次校验都会产生新数组，
                        用引用会让用户刚收起就被重新弹开。
                      */
                      forcedMoreKey={unitProblemForceOpenKey(subjectProblems)}
                      retainedNames={retainedNames}
                      onPatchSubject={(patch) => patchSubject(subject.key, patch)}
                      onMove={(delta) => moveSubject(subject.key, delta)}
                      onDuplicate={() => duplicateSubject(subject.key)}
                      onRemove={() => removeSubject(subject.key)}
                      onAddItem={() => addItem(subject.key)}
                      onPatchItem={(itemKey, patch) => patchItem(subject.key, itemKey, patch)}
                      onRemoveItem={(itemKey) => removeItem(subject.key, itemKey)}
                    />
                  );
                })}

                <Button variant="secondary" onClick={addSubject} disabled={drafts.length >= 20}>
                  ＋ 加一个科目
                </Button>

                {/*
                  📌 这里原本还有最后一段**冗长说明**（"保存是整单覆盖：这一天的作业以你
                  这次提交的为准。孩子已经打过卡的作业项不会被删除，只会从计划里移出并保留
                  记录（到时会有提示）。改名字等于'删掉旧的、新建一个'。"），2026-10-14 按
                  用户要求从界面上**去掉**。

                  ⚠️ 这一段承载的**信息一条都没少**，只是换到了**真正需要它的那一刻**：
                  保存前的移除确认框（下面的 `pendingRemovals` Modal）会逐条列出"这次保存
                  会从这一天的计划里移走哪些科目/作业项"，并在同一处说明"孩子打过卡的项不会
                  被删除、只保留记录"。**不要**因为"表单下面好像少了句提示"又把它加回来
                  —— 那正是用户说的"很啰嗦"。完整解释留给将来的帮助页（见 `HELP_MOVED_TEXTS`）。
                */}
              </section>
            ) : (
              /* ---------- 查看 ---------- */
              <section aria-labelledby="homework-view-heading" className="space-y-4">
                <h2
                  id="homework-view-heading"
                  className="text-sm font-semibold uppercase tracking-wide text-slate-500"
                >
                  {date} 的科目与作业
                </h2>

                {!day?.exists || liveItemCount === 0 ? (
                  <EmptyState
                    icon="📝"
                    title={day?.exists ? '这一天没有需要做的作业' : '这一天还没有作业'}
                    description={
                      canEdit
                        ? '点上面的「复制上次的结构」可以把上一次的科目与作业项搬过来（不会复制完成状态）。'
                        : '你只有查看权限，录入作业需要「可编辑」权限。'
                    }
                    action={
                      canEdit ? (
                        /* 同样走 enterEditMode()：先要一次权威数据再进录入态 */
                        <Button className="mt-2" onClick={enterEditMode}>
                          开始录入
                        </Button>
                      ) : undefined
                    }
                  />
                ) : (
                  <ul className="space-y-4">
                    {liveSubjects.map((subject) => {
                      const done = subject.items.filter((item) => item.status === 'done').length;
                      const abandoned = subject.items.filter(
                        (item) => item.status === 'skipped',
                      ).length;
                      const submitted = subject.items.filter(
                        (item) => item.status === 'submitted',
                      ).length;
                      return (
                        <li key={subject.nodeId}>
                          <Card
                            as="article"
                            className={cn(subject.completion.completed && 'border-emerald-200')}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="flex min-w-0 items-center gap-2">
                                <span aria-hidden="true" className="text-xl">
                                  {iconForName(subject.name, '📘')}
                                </span>
                                <h3 className="truncate text-base font-semibold text-slate-900">
                                  {subject.name}
                                </h3>
                                {subject.completion.completed ? (
                                  <Badge tone="success">✅ 这一科完成</Badge>
                                ) : (
                                  <Badge tone="neutral">
                                    已完成 {done}/{subject.completion.total}
                                    {abandoned > 0 ? `（${abandoned} 项已放弃）` : ''}
                                  </Badge>
                                )}
                                {submitted > 0 && <Badge tone="warn">待验收 {submitted}</Badge>}
                              </div>
                              <p className="shrink-0 text-xs text-slate-500">
                                {describeSubjectSchedule(subject, date)}
                              </p>
                            </div>

                            {subject.completion.completed &&
                              subject.completion.abandoned > 0 && (
                                <p className="mt-2 text-xs text-slate-500">
                                  这一科有 {subject.completion.abandoned} 项被放弃
                                  —— 放弃不阻塞完成，但也不算完成。
                                </p>
                              )}

                            <ul className="mt-3 space-y-2">
                              {subject.items.map((item) =>
                                item.retained ? (
                                  /* ⚠️ retained：看着像"已移除"，并且写清它不会出现在孩子端 */
                                  <li
                                    key={item.habitId}
                                    className="rounded-xl border border-slate-200 bg-slate-50 p-3 opacity-70"
                                  >
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="font-medium text-slate-500 line-through">
                                        {item.name}
                                      </span>
                                      <Badge tone="neutral">已移除（保留记录）</Badge>
                                    </div>
                                    <p className="mt-1 text-xs text-slate-400">
                                      这一项已不在 {date} 的作业计划里，孩子端不会再出现；
                                      它的打卡记录与积分依据仍然保留。
                                    </p>
                                    <ItemStatusLine item={item} />
                                  </li>
                                ) : (
                                  <li
                                    key={item.habitId}
                                    className={cn(
                                      'rounded-xl border border-slate-200 p-3',
                                      item.status === 'done' && 'bg-emerald-50/50',
                                    )}
                                  >
                                    <div className="flex flex-wrap items-center gap-2">
                                      {/*
                                        ⚠️ 这个方框是"这一项有没有打卡记录"的**唯一**依据
                                        （`isHomeworkItemEmpty()`），**不是**"名字新不新"：
                                        同名重新加回来会复用原记录，所以新写上去的名字
                                        也可能立刻显示成"已打过卡"的样子 —— 那是正确行为。
                                      */}
                                      <span aria-hidden="true" className="shrink-0">
                                        {isHomeworkItemEmpty(item) ? '🔲' : '📝'}
                                      </span>
                                      <span className="text-sm font-medium text-slate-900">
                                        {item.name}
                                      </span>
                                    </div>
                                    <ItemStatusLine item={item} />
                                    {/*
                                      ---------- 撤销放弃（仅"已放弃" + 有编辑权） ----------
                                      2026-10-17 新增：`skipped` 以前是死路（家长点错了也只能
                                      看着它一直"已放弃"）。现在这一行给一个退回未完成的入口。
                                      · 判据只看 `item.status === 'skipped'` 与 `canEdit`：
                                        家长恢复**不受日期限制**（见 `decideRestore()`），
                                        所以这里刻意**不**再按日期过滤 —— 那会变成
                                        "前端比后端更严"，把本来能救的历史项挡在外面。
                                      · 不加二次确认：恢复是可逆的安全动作，来回都能再改。
                                      · 真正的门禁在服务端（viewer 家长会拿到 403 文案）。
                                    */}
                                    {canEdit && item.status === 'skipped' && item.checkin?.checkinId && (
                                      <div className="mt-2 flex justify-end border-t border-slate-100 pt-2">
                                        <Button
                                          size="sm"
                                          variant="secondary"
                                          icon={<span aria-hidden="true">↩️</span>}
                                          loading={checkinMutations.restore.isPending}
                                          onClick={() => void restoreItem(item)}
                                        >
                                          {RESTORE_ENTRY.label}
                                        </Button>
                                      </div>
                                    )}
                                    {/* ---------- 验收（仅 submitted + 有权限时渲染） ----------
                                        ⚠️ 「超时」列表里那些 `submitted` 的项渲染的是
                                        **同一个组件**（见 `OverdueRow`）—— 两处一致的保证
                                        来自复用，不是来自"记得同步改两遍"。
                                        ⚠️ 这里**不传** `trailingActions`：这一条作业项只有
                                        「通过 / 打回」两个按钮，布局两个改动前后一个字没变。
                                        三个按钮同一行的那处改动只影响「超时」行 —— 在那里
                                        `OverdueRow` 把「放弃」作为 `trailingActions` 插进来。 */}
                                    <ApprovalActions
                                      target={{
                                        status: item.status,
                                        canApprove: item.canApprove,
                                        pointsPerUnit: item.pointsPerUnit,
                                      }}
                                      busy={approvalBusy}
                                      onApprove={() => approveItem(item)}
                                      onReject={() => rejectItem(item)}
                                    />
                                  </li>
                                ),
                              )}
                            </ul>
                          </Card>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* ---------- 整科被保留（有记录，没法删） ---------- */}
                {retainedSubjects.length > 0 && (
                  <Card className="border-slate-300 bg-slate-50/70">
                    <CardHeader
                      title="已移出计划的科目（保留记录）"
                      description="这些科目下还有孩子的打卡记录，所以整科保留了下来；它们不会出现在孩子端。"
                    />
                    <ul className="space-y-2">
                      {retainedSubjects.map((subject) => (
                        <li key={subject.nodeId} className="rounded-xl border border-slate-200 bg-white p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-slate-500 line-through">
                              {subject.name}
                            </span>
                            <Badge tone="neutral">已移除（保留记录）</Badge>
                            <span className="text-xs text-slate-400">
                              {subject.completion.total} 项 · 完成 {subject.completion.done}
                              {subject.completion.abandoned > 0
                                ? ` · 放弃 ${subject.completion.abandoned}`
                                : ''}
                            </span>
                          </div>
                          <ul className="mt-2 space-y-1">
                            {subject.items.map((item) => (
                              <li key={item.habitId} className="text-xs text-slate-500">
                                <span className="line-through">{item.name}</span>
                                <ItemStatusLine item={item} />
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                    <RetainedNote />
                  </Card>
                )}

                {/* ---------- 清空这一天 ---------- */}
                {canEdit && day?.exists && (
                  <details className="rounded-2xl border border-slate-200 bg-white p-4">
                    <summary className="cursor-pointer text-sm text-slate-500">
                      这一天不布置作业（清空）
                    </summary>
                    <p className="mt-2 text-xs text-slate-500">
                      清空后这一天会变成"还没有录入作业"。已经有打卡记录的作业项只会被移出计划，
                      记录本身不会丢。
                    </p>
                    <Button
                      size="sm"
                      variant="danger"
                      className="mt-3"
                      loading={mutations.save.isPending}
                      onClick={requestClearDay}
                    >
                      清空这一天的作业
                    </Button>
                  </details>
                )}
              </section>
            )}

            {/* ---------- 查过往某天 ---------- */}
            <HistorySection studentId={id} currentDate={date} onPick={setDate} />

            {/* ---------- 手机上也能随时保存 ----------
                放进这一支里（数据已加载）而不是页面根上：骨架屏 / 错误态时
                不该出现一根"保存这一天的作业"。
                ⚠️ 只在「今天的作业」这个标签下出现：超时视图没有可保存的草稿，
                一根"保存这一天的作业"浮在超时列表上只会让人以为它保存的是超时。 */}
            {tab === 'today' && mode === 'edit' && canEdit && (
              <div className="sticky bottom-0 z-20 -mx-4 mt-5 border-t border-slate-200 bg-white/95 px-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:-mx-6 sm:px-6 md:pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">
                    保存会覆盖 {date} 这一整天的作业（孩子已打卡的项不会被删除）
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        // 「撤销改动」= 回到"这一天的真实样子"，所以底稿也跟着回退，
                        // 否则下一次保存的漂移判据会拿一份已被放弃的草稿当基准。
                        // （`day` 就是"这一天的真实样子"：写完之后的响应会被播种进缓存，
                        //  见 `homeworkWriteOnSuccess()` —— 这里不会退回一份更旧的快照。）
                        draftSourceRef.current = day;
                        setDrafts(dayToDraft(day, date));
                        // ⚠️ 上一次校验的提示也要清掉：那些提示按**草稿下标**挂在卡片上，
                        // 草稿换了之后旧下标会指到别的科目（同页其它草稿操作都清）。
                        setProblems([]);
                      }}
                    >
                      撤销改动
                    </Button>
                    <Button
                      loading={mutations.save.isPending}
                      disabled={busy}
                      onClick={() => void doSave()}
                    >
                      保存这一天的作业
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        </div>
          </>
        )}
      </div>

      {/* ---------- 打回重做（原因可选） ----------
          这段弹窗的**内容**搬到了 `components/homework/HomeworkApprovalRejectDialog.tsx`：
          它现在由 `useHomeworkApprovalActions()` 渲染（`approval.rejectDialog`），
          这样「今天的作业」与「超时」两个视图用的是**同一个**弹窗
          （原因上限、"会显示给孩子"、失败时怎么收场，只有一份）。
          打回是**会写到孩子界面上**的动作（原因进 `checkin.note`），
          所以给家长一个弹窗把"要改什么"说清楚；原因留空也允许（等于只说"重做"，
          不动孩子的原备注）。 */}
      {approval.rejectDialog}

      {/* ---------- 保存前的**数据丢失护栏**（见 doSave 的注释） ----------
          ⛔ 这是本页最重要的一道确认：整单覆盖的语义是"提交里没有的 = 从这一天移除"，
          方向是危险的那一边。只要这次保存会移除**当前有效**的科目/作业项，
          就绝不能一声不响地发出去 —— 用户报的就是"我只是加了一科，另一科没了"。

          文案刻意先说"这次保存会把 X 从这一天的作业计划里移除"（不是"你确定要删除吗"）：
          家长的心理预期是"加一科"，这句话要立刻把这个反差摆在他面前。
          第二段再说清"为什么"，并给一条**没有副作用的出路**（再检查一下）。 */}
      <Modal
        open={pendingRemovals !== null}
        onClose={() => setPendingRemovals(null)}
        title={
          pendingRemovalAction === 'clear'
            ? '清空这一天的作业，确认一下？'
            : '这次保存会移除一些作业，确认一下？'
        }
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              className="sm:flex-1"
              onClick={() => setPendingRemovals(null)}
            >
              {pendingRemovalAction === 'clear' ? '不清空了' : '再检查一下'}
            </Button>
            <Button
              variant="danger"
              className="sm:flex-1"
              loading={mutations.save.isPending}
              onClick={() => void confirmRemovals()}
            >
              {pendingRemovalAction === 'clear' ? '确认清空这一天' : '确认移除并保存'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {pendingRemovals && (
            <>
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                ⚠️{' '}
                {pendingRemovalAction === 'clear'
                  ? `${describeHomeworkRemovals(pendingRemovals) || '这一天的作业会被全部移除'}（${date} 这一天的作业）。`
                  : `${describeHomeworkRemovals(pendingRemovals)}（${date} 这一天的作业）。`}
              </p>
              <ul className="space-y-1 text-sm text-slate-700">
                {pendingRemovals.subjects.map((name) => (
                  <li key={`subject:${name}`}>
                    · 整个科目：<strong>{name}</strong>
                  </li>
                ))}
                {pendingRemovals.items.map((entry) => (
                  <li key={`item:${entry.subjectName}/${entry.itemName}`}>
                    · {entry.subjectName} 的作业项：<strong>{entry.itemName}</strong>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-600">
            保存是<strong>整单覆盖</strong>：这一天的作业以表单里提交的为准，
            <strong>表单里没有的科目与作业项就会从这一天的计划里移走</strong>。
            孩子已经打过卡的项不会被删除，只会从计划里移出并保留记录。
          </p>
          {pendingRemovalAction === 'save' ? (
            <p className="text-xs text-slate-500">
              如果你只是想<strong>加一科</strong>、并不想动上面这些内容，请点「再检查一下」，
              回到表单确认它是不是真的不在录入区里（可能是页面没把它显示出来）。
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              清空之后这一天会变成「还没有录入作业」。孩子的打卡记录与积分依据不受影响，
              但这些作业<strong>不会再出现在孩子端</strong>。
            </p>
          )}
        </div>
      </Modal>

      {/* ---------- 「放弃」超时项（**必须先确认**） ----------
          ⛔ 这是家长端唯一能把"孩子永远不做的那一项"从超时队列里清掉的入口：
          没有它，那一项会一直挂在那里（用户原话是"直到完成**或家长点击放弃**"）。

          为什么必须确认：它会**终结**这一项（`skipped`，科目完成判定不再把它算作欠账），
          一句话说不清后果就会点错。确认文案 deliberately 用人话，
          不出现 habit / checkin / skipped 这些术语。

          ⚠️ 2026-10-17 起它**不再是不可逆**的（`POST /checkins/:id/restore` 能退回未完成），
          所以文案补了一句"点错了可以撤回"（`HOMEWORK_SKIP_CONSEQUENCE_TEXT`），
          并且加了与学生端同一套防误触：**默认焦点在「再想想」**、
          「放弃这一项」降到下方单独一行、尺寸小一档 —— 两个大按钮并排正是误触的形状。 */}
      <Modal
        open={skipTarget !== null}
        onClose={() => setSkipTarget(null)}
        title="放弃这一项作业？"
        description={skipTarget ? `${skipTarget.subjectName} · ${skipTarget.habitName}` : undefined}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-3">
            <Button
              variant="secondary"
              fullWidth
              autoFocus
              onClick={() => setSkipTarget(null)}
            >
              再想想
            </Button>
            <div className="flex justify-center">
              <Button
                variant="danger"
                size="sm"
                loading={checkinMutations.skip.isPending}
                onClick={() => void confirmSkip()}
              >
                放弃这一项
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {HOMEWORK_SKIP_CONSEQUENCE_TEXT}
          </p>
          {skipTarget && (
            <p className="text-xs text-slate-500">
              这一项属于 {skipTarget.date} 的作业
              {skipTarget.status === 'submitted'
                ? '，孩子已经交上来等你验收 —— 放弃它等于验收这一步不用做了。'
                : '，孩子还没做完。'}
            </p>
          )}
          <Input
            label="放弃的原因（可选，会显示给孩子）"
            maxLength={200}
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder="例如：这一课老师改到下周再交"
            hint="留空也可以 —— 孩子端一样会显示「家长已放弃这一项，不用再做了」"
          />
          {skipError && (
            <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {skipError}
            </p>
          )}
        </div>
      </Modal>

      {/* ---------- 「复制上次的结构」的确认 ----------
          它是真的会**覆盖**当天作业单的写操作（服务端直接落库），
          而按钮就在录入区上方 —— 手机上很容易误触。
          草稿本来就是空的时不问（那是每天的常规第一步）。

          ⚠️ 两套文案不是修辞：`hasUnsavedChanges` 为 true 时家长**正在手输**，
          那就必须说"你正在录入的内容会被丢弃"（他以为点一下只是"载入模板"，
          不会意识到自己刚敲的字没了）。判定出自 `homeworkCopyGate()`（@fht/shared），
          与"要不要问"是同一个纯函数 —— 不会出现"文案说不会丢、实际丢了"。 */}
      <Modal
        open={pendingCopy !== null}
        onClose={() => setPendingCopy(null)}
        title="用上次的结构覆盖这一天的作业？"
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setPendingCopy(null)}>
              取消
            </Button>
            <Button
              loading={mutations.copy.isPending}
              onClick={() => {
                setPendingCopy(null);
                void doCopyLast();
              }}
            >
              复制并覆盖
            </Button>
          </div>
        }
      >
        {pendingCopy?.hasUnsavedChanges ? (
          <p className="text-sm text-slate-600">
            你现在正在录入、<strong>还没有保存</strong>的内容会被<strong>丢弃</strong>，
            改成"上一次作业单"的科目与作业项结构。
          </p>
        ) : (
          <p className="text-sm text-slate-600">
            这一天的作业会被改成"上一次作业单"的科目与作业项结构。
            表单里现在就是这一天已有的作业、你还没改过它 —— 复制之后这一天的作业以模板为准。
          </p>
        )}
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
          复制过来的只有<strong>结构</strong>（科目名、时刻、作业项名与分值）——
          完成状态不会复制，今天要从头做。孩子已经打过卡的作业项也不会因此被删除，
          只会从计划里移出并保留记录。
        </p>
      </Modal>

      {/* ---------- 校验失败的提示 ----------
          只在一个地方说：科目卡片里逐条列出（`role="alert"`），
          外加一次 Toast 定位到第一处。之前还额外弹了一个"还差一点"的 Modal，
          结果是同一件事同时出现在三处 —— 手机上弹窗一盖，
          被指出的那两行反而看不见了。 */}
    </AppShell>
  );
}

// ================================================================ 历史

/**
 * 过往作业（默认收起，点开才请求）。
 *
 * 为什么默认收起而不是常驻：这一天该做什么是主任务，历史是"翻账"的次要任务。
 * 常驻会让页面在手机上长出一屏无关内容，而家长每次进来都被迫滚过去。
 *
 * 可点行**不加 hover 底色**（沿用目标页的既有做法）：手机上 hover 会"粘"住，
 * 看起来像选中状态。可点性靠文案（「看这一天 →」）与按下态表达。
 */
function HistorySection({
  studentId,
  currentDate,
  onPick,
}: {
  studentId: string;
  currentDate: string;
  onPick: (date: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const range = useMemo(() => defaultHistoryRange(), []);
  const historyQuery = useHomeworkHistory(open ? studentId : null, range);
  const sheets: HomeworkHistorySheet[] = historyQuery.data?.sheets ?? [];

  return (
    <section aria-labelledby="homework-history-heading">
      <Card>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <span className="min-w-0">
            <span
              id="homework-history-heading"
              className="block text-base font-semibold text-slate-900"
            >
              查过往的作业
            </span>
            <span className="mt-0.5 block text-sm text-slate-500">
              最近 30 天每天布置了什么、完成了多少；点一天就翻到那天。
            </span>
          </span>
          <span aria-hidden="true" className="shrink-0 text-slate-400">
            {open ? '▲' : '▼'}
          </span>
        </button>

        {open && (
          <div className="mt-4">
            {historyQuery.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : historyQuery.isError ? (
              <ErrorState
                compact
                message={toErrorMessage(historyQuery.error)}
                onRetry={() => void historyQuery.refetch()}
              />
            ) : sheets.length === 0 ? (
              <p className="rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-600">
                最近 30 天还没有录入过作业。
              </p>
            ) : (
              <>
                <p className="mb-2 text-xs text-slate-500">
                  {historyQuery.data?.from} ~ {historyQuery.data?.to}：
                  {historyQuery.data?.summary.sheetCount ?? 0} 天有作业，共{' '}
                  {historyQuery.data?.summary.itemCount ?? 0} 项，完成{' '}
                  {historyQuery.data?.summary.doneCount ?? 0} 项
                  {(historyQuery.data?.summary.abandonedCount ?? 0) > 0
                    ? `（另有 ${historyQuery.data?.summary.abandonedCount} 项已放弃）`
                    : ''}
                  {historyQuery.data?.clamped ? '（区间超过 366 天，已收敛）' : ''}
                </p>
                <ul className="divide-y divide-slate-100">
                  {sheets.map((sheet) => (
                    <li key={sheet.sheetNodeId}>
                      <button
                        type="button"
                        onClick={() => onPick(sheet.date)}
                        className={cn(
                          'flex w-full flex-wrap items-center justify-between gap-2 py-3 text-left',
                          'rounded-xl px-2 active:bg-slate-50',
                          sheet.date === currentDate && 'bg-brand-50',
                        )}
                      >
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-slate-900">{sheet.date}</span>
                            {sheet.date === currentDate && <Badge tone="brand">正在看这天</Badge>}
                            {sheet.completed ? (
                              <Badge tone="success">✅ 全部完成</Badge>
                            ) : (
                              <Badge tone="neutral">
                                完成 {sheet.doneCount}/{sheet.itemCount}
                              </Badge>
                            )}
                            {sheet.submittedCount > 0 && (
                              <Badge tone="warn">待验收 {sheet.submittedCount}</Badge>
                            )}
                            {sheet.abandonedCount > 0 && (
                              <Badge tone="neutral">放弃 {sheet.abandonedCount}</Badge>
                            )}
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">
                            {sheet.subjectCount} 个科目 · {sheet.itemCount} 项作业
                            {sheet.remainingCount > 0 ? ` · 还剩 ${sheet.remainingCount} 项` : ''}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-medium text-brand-700">
                          看这一天 →
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Card>
    </section>
  );
}

// ================================================================ 超时

/**
 * 「超时」视图（家长端）。
 *
 * ## 它解决的是什么
 *
 * 用户最早的需求：「没有完成的作业任务，会一直显示，在「超时」标签中显示，
 * **直到完成或家长点击放弃**」。前面两半（"一直显示"与"标签"）学生端已有，
 * 家长端缺的是**「放弃」这个出口** —— 孩子永远不做的那一项，如果没有它，
 * 就会一直挂在超时里，谁也清不掉（服务端刻意不把跨天未完成的作业结算成 `missed`：
 * 那是"一次性任务"的语义，用在这里等于替孩子宣布放弃）。
 *
 * ## 三条口径（与学生端**必须一致**，靠复用同一批实现保证）
 *
 * 1. **排序**：`due_at` 倒序（最新截止在最上面）由**服务端**保证
 *    （`listOverdueCheckins`），前端**不重排** —— 两端都排就再也说不清谁说了算。
 * 2. **判据**：`due_at < now()` 且状态未终结（`pending / running / paused /
 *    auto_paused / submitted`）。⚠️ 家长没填截止时刻时科目的 `due_time` 是
 *    服务端落下的 23:59（"当天完成"），所以"当天截止"的项**第二天**才进超时 ——
 *    这是**预期行为**（某天还没过完就不该出现在超时里），不要"修"它。
 * 3. **呈现**：每一行都是 `components/homework/OverdueRow`（与学生端**同一个组件**），
 *    「已超时 X」的文案来自 `@fht/shared` 的 `formatOverdueDuration()`。
 *    家长端比学生端多两组动作：**「放弃」**（`onSkip`，这一屏独有）与
 *    **「通过 / 打回」**（`onApprove` / `onReject`，与「今天的作业」共用同一个
 *    `ApprovalActions` 与同一个 `useHomeworkApprovalActions()`）。
 *
 *    ⚠️ 2026-10-15 第二次用户实测：这三组动作当时**排成了两行**（"放弃"在第二行），
 *    因为每一组各自一个 flex 容器。现在 `OverdueRow` 把「放弃」作为
 *    `trailingActions` 传进 `ApprovalActions` 的**同一个**按钮行，所以
 *    「通过 / 打回 / 放弃」同属一行；窄屏真的放不下时由那个按钮行的
 *    `flex-wrap` 优雅换行（不缩小按钮、不横向溢出）。
 *    ⛔ 这个页面（`OverdueSection` / 「今天的作业」）**不要**再给按钮另起容器。
 *
 * ## 为什么这里**没有**「开始 / 完成」，但**有**「通过 / 打回」
 *
 * 家长的主路径是"看有没有欠账 + 决定要不要放弃 / 验收"，而不是替孩子计时
 * （孩子点完成只到 `submitted`，反而多出一条待验收）。要替孩子动手就点「今天」标签、
 * 翻到那一天去操作 —— 那里已经在渲染完整的状态与计时入口。
 *
 * ⚠️ **但验收必须在场**（2026-10-15 用户实测的 bug）：昨天提交、今天才超时的作业项
 * **只出现在这个列表里**，家长想点「通过」时"却没有按钮了"。所以这一屏
 * 每一行都渲染 `OverdueRow`，而它会用**同一个** `ApprovalActions` 渲染验收动作
 * —— 只对 `submitted` + 有权限的项，其余（等孩子做、已终结）什么按钮都不多给。
 */
function OverdueSection({
  entries,
  query,
  canSkip,
  busy,
  kid,
  queue = 'overdue',
  onSkip,
  onApprove,
  onReject,
}: {
  entries: HomeworkQueueEntry[];
  query: {
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  /** 有没有放弃权限（家长 owner/editor）。没有就只读 —— 不渲染会失败的按钮。 */
  canSkip: boolean;
  busy: boolean;
  kid: boolean;
  /**
   * 这一屏是**哪一条队列**：
   *  - `'overdue'`（默认）：已经过了截止时刻（`due_at < now`）的欠账；
   *  - `'open'`（「**还有效**」，2026-10-17 新增）：还没到期、但**不是今天布置**的项。
   *
   * ⚠️ 两支的**动作完全相同**（通过 / 打回 / 放弃），差别只有文案与排序说明，
   * 所以这里只有一个组件、一个 `queue` 开关 —— ⛔ 不要再复制一份。
   * 「还有效」那一支的存在理由就是用户报的缝：周五布置、周日截止的两项作业，
   * 周六在「今天的作业」（按布置日筛）和「超时」（按截止时刻筛）里都看不见。
   */
  queue?: 'overdue' | 'open';
  onSkip: (entry: HomeworkQueueEntry) => void;
  /**
   * 验收（通过 / 打回）—— 家长端会传（`useHomeworkApprovalActions`），
   * 学生端不传（学生不能验收，见 `canApproveCheckin`）。
   */
  onApprove?: (entry: HomeworkQueueEntry) => void;
  onReject?: (entry: HomeworkQueueEntry) => void;
}) {
  const isOpen = queue === 'open';
  if (query.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        message={toErrorMessage(query.error)}
        onRetry={() => void query.refetch()}
      />
    );
  }

  if (entries.length === 0) {
    return isOpen ? (
      <EmptyState
        icon="🌤️"
        title="没有还没到期的作业"
        description="昨天、前天布置、但截止日期还在后面的作业会留在这里，直到孩子做完、或者你点「放弃」。现在是空的。"
      />
    ) : (
      <EmptyState
        icon="🎉"
        title="没有超时的作业"
        description="过了截止时间还没做完的作业会一直留在这里，直到孩子做完、或者你点「放弃」。现在是空的，说明没有欠账。"
      />
    );
  }

  return (
    <section
      aria-labelledby={isOpen ? 'homework-open-heading' : 'homework-overdue-heading'}
      className="space-y-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id={isOpen ? 'homework-open-heading' : 'homework-overdue-heading'}
          className="text-sm font-semibold uppercase tracking-wide text-slate-500"
        >
          {isOpen ? '还有效（还没到期）' : '超时未完成'}
        </h2>
        <span className="text-xs text-slate-400">
          {isOpen ? '按截止时间排序，越快到期越靠前' : '按截止时间排序，最新的在最上面'}
        </span>
      </div>

      <p
        className={cn(
          'rounded-xl px-3 py-2 text-xs',
          isOpen ? 'bg-sky-50 text-sky-900' : 'bg-amber-50 text-amber-800',
        )}
      >
        {isOpen ? (
          <>
            这些作业<strong>还没到期</strong>，而且不是今天布置的 —— 孩子那边现在就能做，
            做完点「完成」交给你验收，它才会从这里消失。孩子交上来、你还没看的那些，
            直接在下面点「<strong>通过</strong>」或「打回」就行（不用翻回那一天）。
            孩子一直不做的那一项，可以点「<strong>放弃</strong>」收掉（会先跟你确认一次）。
          </>
        ) : (
          <>
            这些作业已经过了截止时间，但<strong>仍然可以做</strong> ——
            孩子做完点「完成」交给你验收，它才会从这里消失。
            孩子交上来、你还没看的那些，直接在下面点「<strong>通过</strong>」或「打回」就行
            （不用翻回那一天）。孩子一直不做的那一项，可以点「<strong>放弃</strong>」收掉
            （会先跟你确认一次）。跨天的欠账都在这里，与上面的「今天的作业」<strong>分开</strong>。
          </>
        )}
      </p>

      {!canSkip && (
        <p className="rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-600">
          你在这个孩子上的权限是<strong>只读</strong>，「放弃」与「通过 / 打回」只有
          「可编辑 / 所有者」能点。联系孩子的家长把自己加到可编辑的协作者里就能操作。
        </p>
      )}

      <ul className="space-y-3">
        {entries.map((entry) => (
          <OverdueRow
            key={entry.id}
            entry={entry}
            queue={queue}
            openBadge={entry.badge}
            kid={kid}
            busy={busy}
            canSkip={canSkip}
            parentView
            onSkip={() => onSkip(entry)}
            onApprove={onApprove ? () => onApprove(entry) : undefined}
            onReject={onReject ? () => onReject(entry) : undefined}
          />
        ))}
      </ul>
    </section>
  );
}
